/**
 * Song Workspace — devotional song ingest + media toolbox.
 *
 * Three tabs: YouTube ingest (paste a URL, the FastAPI backend pulls
 * MP3 + lyrics + translation + practice sheet to Drive), Voice Drop
 * (share or record a voice note for transcription), Media Toolbox
 * (audio merge / cross-fade / trim, video composer, image square).
 *
 * Adapted from the home-and-library bundle to render UNDER the existing
 * AppShell. Two adjustments from the bundle original:
 *   • createFileRoute path is "/_app/library/song-workspace" (was top-
 *     level "/library/song-workspace") so it inherits the auth gate,
 *     sidebar nav, and bottom tabs.
 *   • The bundle's <Toaster> import + mount was removed — __root.tsx
 *     already mounts a global Toaster. Two instances would double-fire
 *     every toast.
 *
 * Backend: a FastAPI server. URL defaults to VITE_FASTAPI_URL
 * (build-time) or "http://localhost:8000" and the user can override
 * via the inline API settings dropdown. Until you stand up a backend
 * the action buttons will surface a clear error toast.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  Youtube, Star, Loader2, Check, Sparkles, Settings2, AlertCircle, Save,
  Smartphone, Upload, Play, Music2, Video, Image as ImageIcon,
  Layers, Scissors, CloudUpload, FolderTree, RefreshCw, FolderPlus, ChevronRight,
  ChevronLeft, FileAudio, FileText, Languages, FileMusic, ExternalLink, X, Download,
  // #250 — editable cells + rating + verse flags
  Pencil, ThumbsUp, ThumbsDown,
} from "lucide-react";
// #250 — Song feedback (rating + per-verse translation edits + verse flags).
// #254 — extended with getEditedLyrics for the source-script column.
import {
  EMPTY_FEEDBACK,
  fetchFeedback,
  patchFeedback,
  getEditedTranslation,
  getEditedLyrics,
  getVerseFlag,
  type SongFeedback,
  type VerseFlag,
} from "@/lib/song-feedback";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { checkTranslationLang, createLangMismatchTracker } from "@/lib/translation-lang-check";
import { getDefaultDriveFolder, getDefaultTarget, setDefaultDriveFolder } from "@/lib/song-defaults";
import { AudioRecorder } from "@/components/AudioRecorder";
import { RangeTrimmer } from "@/components/RangeTrimmer";
import { useSession } from "@/hooks/use-session";
import { useIsMobile } from "@/hooks/use-mobile";

/* ------------------------------------------------------------------------
 * INAUGURAL_YOUTUBE — the very first song Uma wants the Mac-worker
 * pipeline to handle. The "Try the inaugural song" pill in the YouTube
 * tab prefills this URL so the first run is one tap, not a copy-paste.
 *
 * Keep this constant in one place — if the test track changes, edit
 * here. Mobile gets the same prompt but it links the user back to
 * desktop because the queue still needs the Mac daemon to claim.
 * ---------------------------------------------------------------------- */
const INAUGURAL_YOUTUBE = "https://www.youtube.com/watch?v=1tuQfRaP9b0";

export const Route = createFileRoute("/_app/library_/song-workspace")({
  head: () => ({
    meta: [
      { title: "Add a Song — Ojaswati" },
      { name: "description", content: "Ingest, transliterate, translate and archive devotional songs to Google Drive." },
    ],
  }),
  component: SongWorkspace,
});

/* ---------- Theme (scoped slate + emerald) ---------- */
const themeStyle: CSSProperties = {
  ["--background" as any]: "oklch(0.205 0.03 256)",       // ~#0f172a slate-900
  ["--foreground" as any]: "oklch(0.97 0.01 250)",
  ["--card" as any]: "oklch(0.245 0.028 256)",
  ["--card-foreground" as any]: "oklch(0.97 0.01 250)",
  ["--popover" as any]: "oklch(0.245 0.028 256)",
  ["--popover-foreground" as any]: "oklch(0.97 0.01 250)",
  ["--primary" as any]: "oklch(0.72 0.14 162)",           // ~#10b981 emerald
  ["--primary-foreground" as any]: "oklch(0.18 0.03 256)",
  ["--secondary" as any]: "oklch(0.3 0.028 256)",
  ["--secondary-foreground" as any]: "oklch(0.97 0.01 250)",
  ["--muted" as any]: "oklch(0.28 0.025 256)",
  ["--muted-foreground" as any]: "oklch(0.72 0.02 250)",
  ["--accent" as any]: "oklch(0.35 0.05 170)",
  ["--accent-foreground" as any]: "oklch(0.97 0.01 250)",
  ["--border" as any]: "oklch(1 0 0 / 8%)",
  ["--input" as any]: "oklch(1 0 0 / 10%)",
  ["--ring" as any]: "oklch(0.72 0.14 162)",
  background:
    "radial-gradient(1200px 600px at 20% -10%, oklch(0.28 0.05 170 / 0.35), transparent 60%)," +
    "radial-gradient(900px 500px at 100% 0%, oklch(0.28 0.06 264 / 0.35), transparent 60%)," +
    "oklch(0.205 0.03 256)",
  color: "oklch(0.97 0.01 250)",
  colorScheme: "dark",
  minHeight: "100vh",
};

/* ---------- Shared types ---------- */
const SCRIPTS = ["English (IAST)", "Tamil", "Sanskrit (Devanagari)"] as const;
type Script = (typeof SCRIPTS)[number];
type Target = "english" | "tamil" | "sanskrit";

/* ---------- Song languages ----------
 * Display label (what the user sees) → ISO 639-1 code that whisper-cli
 * understands (-l ta, -l hi, etc.). Auto-detect is the safe default but
 * pinning the language massively improves transcription accuracy for
 * short songs or songs that start with a non-target-language intro.
 *
 * If a song is mixed-language (e.g. Sanskrit verses with Tamil
 * announcements), pick the dominant one. Whisper will still pick up
 * isolated phrases in other languages within each segment.
 */
const SONG_LANGUAGES = [
  { label: "Auto-detect", iso: "" },
  { label: "Tamil",       iso: "ta" },
  { label: "Hindi",       iso: "hi" },
  { label: "Sanskrit",    iso: "sa" },
  { label: "Telugu",      iso: "te" },
  { label: "Kannada",     iso: "kn" },
  { label: "Malayalam",   iso: "ml" },
  { label: "Bengali",     iso: "bn" },
  { label: "Gujarati",    iso: "gu" },
  { label: "Marathi",     iso: "mr" },
  { label: "Punjabi",     iso: "pa" },
  { label: "Odia",        iso: "or" },
  { label: "English",     iso: "en" },
] as const;
type SongLanguage = (typeof SONG_LANGUAGES)[number]["label"];

/* ---------- Song metadata ----------
 * Replaces the v207 auto-filled blueprint (which stamped
 * "Untitled Composition", "Sanskrit", "Traditional", "—" on every paste
 * and was the source of the wrong-metadata user complaint).
 *
 * Title is required. Language is the only field that influences the
 * pipeline (passed to whisper.cpp via the Mac daemon for a better
 * transcription). The rest are recorded for Drive filename, ID3 tags,
 * Drive custom properties, and Library search — none gate the pipeline.
 */
type SongMeta = {
  title:    string;
  language: SongLanguage;
  composer: string;
  raga:     string;
  album:    string;
  year:     string;
};
/* #227 — Language defaults to "Sanskrit" rather than "Auto-detect"
 * because >90% of this user's catalogue is devotional Sanskrit (LIC
 * songs, bhajans, stotras). #232 — Detection from the YouTube title
 * upgrades that default per-song (Tamil scripts win, then keywords).
 * Pinning the language dramatically improves whisper.cpp transcription
 * accuracy vs. auto-detect. */
const EMPTY_META: SongMeta = {
  title: "", language: "Sanskrit", composer: "", raga: "", album: "", year: "",
};

/* #232 — Heuristic language detector for YouTube titles + author.
 *
 * Runs after oEmbed responds. Inspects the song title (and author
 * channel name as a secondary hint) for:
 *
 *   1. Tamil script characters (Unicode 0B80–0BFF). Strongest signal —
 *      ANY Tamil character in the title flips the dropdown to Tamil.
 *   2. Devanagari script (Unicode 0900–097F). Ambiguous between
 *      Sanskrit and Hindi but in this user's library effectively
 *      means Sanskrit (stotras / bhajans typeset in Devanagari).
 *   3. Explicit English words ("Tamil"/"Sanskrit"/"Hindi"/etc.).
 *   4. Common transliterated keywords per language.
 *
 * Falls through to null → caller keeps the prior default (Sanskrit).
 * The user can always override via the dropdown — we never lock the
 * choice, we just pick a smarter starting value than "always
 * Sanskrit". */
function detectLanguageFromTitle(
  title: string,
  authorName?: string,
): SongLanguage | null {
  const hay = `${title || ""} ${authorName || ""}`;
  if (!hay.trim()) return null;
  // 1) Tamil script.
  if (/[஀-௿]/.test(hay)) return "Tamil";
  // 2) Devanagari → assume Sanskrit (this user's library context).
  if (/[ऀ-ॿ]/.test(hay)) return "Sanskrit";
  // 3) Explicit language words. Case-insensitive whole-word match so
  //    "Sanskrit" matches but "transanskripted" does not.
  const lower = hay.toLowerCase();
  const explicit: { lang: SongLanguage; re: RegExp }[] = [
    { lang: "Tamil",     re: /\btamil\b/ },
    { lang: "Sanskrit",  re: /\bsanskrit\b/ },
    { lang: "Hindi",     re: /\bhindi\b/ },
    { lang: "Telugu",    re: /\btelugu\b/ },
    { lang: "Kannada",   re: /\bkannada\b/ },
    { lang: "Malayalam", re: /\bmalayalam\b/ },
    { lang: "Bengali",   re: /\bbengali\b/ },
    { lang: "Gujarati",  re: /\bgujarati\b/ },
    { lang: "Marathi",   re: /\bmarathi\b/ },
    { lang: "Punjabi",   re: /\bpunjabi\b/ },
    { lang: "Odia",      re: /\bodia\b/ },
    { lang: "English",   re: /\benglish\b/ },
  ];
  for (const { lang, re } of explicit) {
    if (re.test(lower)) return lang;
  }
  // 4) Transliterated keyword hints. Conservative whitelist — only
  //    SONG-FORM keywords that are unambiguously Tamil. Singer /
  //    composer NAMES (sooryagayathri, saraswathi, sundari) are
  //    terrible signals: Sooryagayathri is a Sanskrit/Karnatic singer
  //    even though she's South Indian; "Saraswati" appears in
  //    Swami Dayanand SARASWATI (Sanskrit/Vedanta teacher). The old
  //    heuristic was incorrectly flagging famous Sanskrit songs as
  //    Tamil based on the artist's regional name. User had to
  //    constantly re-pick Sanskrit manually. Removed all proper-name
  //    triggers — keep only Tamil-religious-text keywords that don't
  //    appear in non-Tamil contexts.
  const tamilHints = [
    "thirumurai", "thiruppavai",
    "thalattu", "padal", "porul",
  ];
  if (tamilHints.some((k) => lower.includes(k))) return "Tamil";
  const sanskritHints = [
    "stotram", "stotra", "shloka", "ashtakam", "vandanam",
    "namaskaram", "namah", "vandanam", "suprabhatam", "kavacham",
    "sahasranama", "sahasranam", "panchakam", "dwadashakam",
  ];
  if (sanskritHints.some((k) => lower.includes(k))) return "Sanskrit";
  // Couldn't tell — let caller keep its default.
  return null;
}

// #237 — Smart default: in production (non-localhost host) fall back to
// the Fly URL instead of localhost. Previously a fresh browser profile
// would try to fetch from http://localhost:8000 and Safari blocked
// the mixed-content request → "Load failed" on the Songs page.
import { fastapiBase as _fastapiBase } from "@/lib/fastapi-url";
const DEFAULT_API = _fastapiBase();

// Shared API-key header. The backend enforces it on every endpoint when
// OJASWATI_API_KEY is set (e.g. on Fly.io). Locally the backend leaves
// auth off so an empty key here just means we send an empty header that
// the no-op dependency ignores.
const API_KEY_STORAGE = "ojaswati_api_key";
const API_KEY_HEADER = "X-Ojaswati-Key";

/**
 * Build a Headers object pre-loaded with the API key from localStorage.
 * Pass extra entries (Content-Type, Accept, …) and they win.
 * The key is read fresh on every call — no need to thread it through
 * props or React state in callers that are far from the settings UI.
 */
function apiHeaders(extra?: Record<string, string>): Headers {
  const h = new Headers(extra);
  try {
    const k = typeof window !== "undefined" ? localStorage.getItem(API_KEY_STORAGE) : null;
    if (k) h.set(API_KEY_HEADER, k);
  } catch {
    /* localStorage blocked — send no key, backend returns 401 if it requires one */
  }
  return h;
}

const YT_STEPS = [
  "Downloading High-Quality MP3…",
  "Extracting Metadata & Thumbnails…",
  "Compiling Selected Script Lyrics…",
  "Running Ollama Translation…",
  "Uploading MP3 & Practice Sheet to Google Drive Workspace!",
];
type StepStatus = "pending" | "running" | "done" | "error";

/* ---------- Drive artifact stepper ---------- */
type DriveArtifactId = "audio_mp3" | "lyrics" | "translation" | "practice_sheet";
type DriveArtifact = {
  id: DriveArtifactId;
  label: string;
  hint: string;
  status: StepStatus;
  message?: string;
  url?: string;
};
const DRIVE_ARTIFACT_DEFS: ReadonlyArray<Omit<DriveArtifact, "status">> = [
  { id: "audio_mp3",      label: "Audio MP3",       hint: "320 kbps · normalized" },
  { id: "lyrics",         label: "Lyrics",          hint: "Selected scripts · .txt + .pdf" },
  { id: "translation",    label: "Translation",     hint: "Target language · .pdf" },
  { id: "practice_sheet", label: "Practice Sheet",  hint: "Bundled metadata + chords" },
];
const blankDriveSteps = (): DriveArtifact[] =>
  DRIVE_ARTIFACT_DEFS.map((d) => ({ ...d, status: "pending" }));

/* ---------- Root ---------- */
function SongWorkspace() {
  const [apiUrl, setApiUrl] = useState(DEFAULT_API);
  const [apiKey, setApiKey] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  // Mobile boots into Voice Drop — YouTube ingest needs the Mac
  // daemon so it's not the right first tab on a phone. Users can
  // still tap into YouTube to see the "open on your laptop" hint.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const savedUrl = localStorage.getItem("fastapi_url");
    if (savedUrl) setApiUrl(savedUrl);
    const savedKey = localStorage.getItem(API_KEY_STORAGE);
    if (savedKey) setApiKey(savedKey);
  }, []);

  const saveApi = (v: string) => {
    setApiUrl(v);
    try { localStorage.setItem("fastapi_url", v); } catch {}
  };
  const saveKey = (v: string) => {
    setApiKey(v);
    try {
      if (v) localStorage.setItem(API_KEY_STORAGE, v);
      else localStorage.removeItem(API_KEY_STORAGE);
    } catch {}
  };

  return (
    <div className="dark" style={themeStyle}>
      <div className="min-h-screen bg-background text-foreground">
        {/* Top bar */}
        <header className="sticky top-0 z-20 border-b border-border bg-background/70 backdrop-blur-xl">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {/* #228 — Back-nav so the user always has an obvious path
                  to the queue/status page. The sidebar "Songs" link
                  now lands on /library/songs, and this workspace is
                  the "add new" sub-flow. The "←" makes that hierarchy
                  visible from inside the workspace. */}
              <Link
                to="/library/songs"
                className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1.5 shrink-0"
                title="Back to Songs"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Songs
              </Link>
              <div className="h-9 w-9 rounded-xl bg-primary/15 border border-primary/30 grid place-items-center shrink-0">
                <Music2 className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg leading-none truncate">Add a Song</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  Paste a YouTube URL — we'll do the rest in the background.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <span
                  className={
                    "h-1.5 w-1.5 rounded-full animate-pulse " +
                    (apiKey ? "bg-primary" : "bg-amber-500")
                  }
                  title={apiKey ? "API key set" : "No API key — required for the Fly.io deploy"}
                />
                FastAPI
              </span>
              <button
                onClick={() => setShowSettings((v) => !v)}
                className="text-xs inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground border border-border rounded-md px-2.5 py-1.5"
              >
                <Settings2 className="h-3.5 w-3.5" /> API
              </button>
            </div>
          </div>
          {showSettings && (
            <div className="border-t border-border bg-card/40">
              <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 space-y-3 max-w-md">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    FastAPI base URL
                  </label>
                  <Input
                    value={apiUrl}
                    onChange={(e) => saveApi(e.target.value)}
                    placeholder="http://localhost:8000"
                    className="mt-1 font-mono text-xs bg-background/40"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                    API key
                    <span className="font-mono text-[10px] text-muted-foreground/70">
                      (X-Ojaswati-Key)
                    </span>
                  </label>
                  <Input
                    type="password"
                    value={apiKey}
                    onChange={(e) => saveKey(e.target.value)}
                    placeholder="paste the secret from `fly secrets`"
                    className="mt-1 font-mono text-xs bg-background/40"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Leave blank for local backends with no auth. Required when
                    pointing at the Fly.io deploy. Stored only in this browser.
                  </p>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Endpoints: <span className="font-mono text-primary">{apiUrl}/ingest</span>,{" "}
                  <span className="font-mono text-primary">{apiUrl}/whatsapp</span>,{" "}
                  <span className="font-mono text-primary">{apiUrl}/drive/*</span>
                </p>
              </div>
            </div>
          )}
        </header>

        <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
          {/* Mobile default is Voice Drop — YouTube needs the Mac daemon,
              so dropping a phone user straight into the URL bar would
              dead-end them. Desktop stays on YouTube as the headline flow.
              #273 — When the page loads via the PWA share target
              (?shared=1 in URL — WhatsApp / Voice Memos / Telegram →
              ojaswati), force the Voice Drop tab so the incoming audio
              lands in the right tab regardless of viewport. */}
          <Tabs
            defaultValue={
              (typeof window !== "undefined" &&
              new URLSearchParams(window.location.search).get("shared") === "1")
                ? "whatsapp"
                : isMobile
                  ? "whatsapp"
                  : "youtube"
            }
            className="w-full"
          >
            <TabsList className="w-full grid grid-cols-3 h-auto p-1 bg-card/60 border border-border rounded-xl">
              <TabsTrigger value="youtube" className="py-2.5 data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-none gap-1.5">
                <Youtube className="h-4 w-4" />
                <span className="hidden sm:inline">YouTube Ingest</span>
                <span className="sm:hidden">YouTube</span>
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="py-2.5 data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-none gap-1.5">
                <Smartphone className="h-4 w-4" />
                <span className="hidden sm:inline">Voice Drop</span>
                <span className="sm:hidden">Drop</span>
              </TabsTrigger>
              <TabsTrigger value="toolbox" className="py-2.5 data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-none gap-1.5">
                <Wrench className="h-4 w-4" />
                <span className="hidden sm:inline">Media Toolbox</span>
                <span className="sm:hidden">Toolbox</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="youtube" className="mt-6">
              <YouTubeTab apiUrl={apiUrl} />
            </TabsContent>
            <TabsContent value="whatsapp" className="mt-6">
              <WhatsAppTab apiUrl={apiUrl} />
            </TabsContent>
            <TabsContent value="toolbox" className="mt-6">
              <ToolboxTab />
            </TabsContent>
          </Tabs>
        </main>
        {/* Toaster removed — __root.tsx mounts a global one. */}
      </div>
    </div>
  );
}

// inline Wrench import alias to avoid lucide name conflict
function Wrench(props: React.SVGProps<SVGSVGElement>) {
  return <Layers {...props} />;
}

/* ---------- Reusable bits ---------- */
function StarRating({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          className="p-1"
          aria-label={`${n} star`}
        >
          <Star
            className={cn(
              "h-5 w-5 transition",
              (hover || value) >= n ? "fill-primary text-primary" : "text-muted-foreground/50"
            )}
          />
        </button>
      ))}
    </div>
  );
}

/* #272 — Single-select script picker. Per user feedback:
 *   "script/lyrics should be in one language only"
 * Behaves as a radio group: exactly one chip is always active. Clicking
 * the active chip is a no-op (so the user can never end up with zero
 * selected, which would block the Sync button). Clicking another chip
 * swaps. The `onToggle` callback contract is unchanged so existing
 * call sites keep working — the parent's setter now does a full replace
 * instead of a multi-toggle. */
function ScriptChips({ scripts, onToggle }: { scripts: Script[]; onToggle: (s: Script) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Lyrics script">
      {SCRIPTS.map((s) => {
        const active = scripts.includes(s);
        return (
          <button
            key={s}
            role="radio"
            aria-checked={active}
            onClick={() => {
              if (active) return; // radio semantics — can't deselect
              onToggle(s);
            }}
            className={cn(
              "px-3.5 py-1.5 rounded-full text-xs border transition",
              active
                ? "bg-primary/15 text-primary border-primary/40"
                : "bg-background/40 text-muted-foreground border-border hover:text-foreground"
            )}
          >
            {active && <Check className="inline h-3 w-3 mr-1 -mt-0.5" />}
            Lyrics in {s}
          </button>
        );
      })}
    </div>
  );
}

function TargetSegmented({ target, onChange }: { target: Target; onChange: (t: Target) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-background/40 p-1">
      {([
        ["english", "Translate to English"],
        ["tamil", "Translate to Tamil"],
        ["sanskrit", "Translate to Sanskrit"],
      ] as const).map(([id, label]) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={cn(
            "px-4 py-1.5 text-xs rounded-md transition",
            target === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/* #227 — Simplified metadata card.
 * Title + Language stay always visible (the only two fields that
 * matter for the pipeline). Composer/Raga/Album/Year/Curate live
 * behind a collapsible "Add more details" disclosure so the form
 * fits one screen and the user doesn't feel pressured to fill them. */
function SongMetadataCard({
  meta,
  setMeta,
  rating,
  setRating,
  metaReady,
  titleFetching = false,
  onLanguagePick,
}: {
  meta: SongMeta;
  setMeta: React.Dispatch<React.SetStateAction<SongMeta>>;
  rating: number;
  setRating: (n: number) => void;
  metaReady: boolean;
  titleFetching?: boolean;
  /* #232 — Fires when the user explicitly picks a language from the
   * dropdown. Parent flips a ref so auto-detection won't overwrite
   * the choice on a subsequent oEmbed re-fire. */
  onLanguagePick?: () => void;
}) {
  // Auto-expand the disclosure if ANY optional field already has a
  // value — so opening a previously-filled form doesn't hide their
  // existing data. Stays collapsed by default for a fresh form.
  const hasOptional = !!(
    meta.composer.trim() ||
    meta.raga.trim() ||
    meta.album.trim() ||
    meta.year.trim() ||
    rating > 0
  );
  const [moreOpen, setMoreOpen] = useState(hasOptional);
  return (
    <SectionCard
      title="Song"
      subtitle="Title is auto-filled from YouTube. Language defaults to Sanskrit — change it if needed."
    >
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Song Title *">
          <div className="relative">
            <Input
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              placeholder={
                titleFetching
                  ? "Fetching title from YouTube…"
                  : "Pasting a YouTube URL above will auto-fill this"
              }
              className="bg-background/40 pr-9"
            />
            {titleFetching && (
              <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-primary" />
            )}
          </div>
        </Field>
        <Field label="Language">
          <select
            value={meta.language}
            onChange={(e) => {
              setMeta({ ...meta, language: e.target.value as SongLanguage });
              onLanguagePick?.();
            }}
            className="h-9 w-full rounded-md border border-border bg-background/40 px-2 text-sm"
          >
            {SONG_LANGUAGES.map((l) => (
              <option key={l.label} value={l.label}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <button
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronRight
          className={cn("h-3 w-3 transition", moreOpen && "rotate-90")}
        />
        {moreOpen ? "Hide" : "Add"} composer, raga, album, year & rating
      </button>

      {moreOpen && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-border/50">
          <Field label="Composer">
            <Input
              value={meta.composer}
              onChange={(e) => setMeta({ ...meta, composer: e.target.value })}
              placeholder="e.g. Dayananda Saraswati"
              className="bg-background/40"
            />
          </Field>
          <Field label="Raga (Musical Scale)">
            <Input
              value={meta.raga}
              onChange={(e) => setMeta({ ...meta, raga: e.target.value })}
              placeholder="e.g. Revati"
              className="bg-background/40"
            />
          </Field>
          <Field label="Album / Collection">
            <Input
              value={meta.album}
              onChange={(e) => setMeta({ ...meta, album: e.target.value })}
              placeholder="e.g. Stotram Series"
              className="bg-background/40"
            />
          </Field>
          <Field label="Year">
            <Input
              value={meta.year}
              onChange={(e) => setMeta({ ...meta, year: e.target.value })}
              placeholder="e.g. 1995"
              inputMode="numeric"
              className="bg-background/40"
            />
          </Field>
          <div className="sm:col-span-2 flex items-center gap-3 pt-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Curate
            </span>
            <StarRating value={rating} onChange={setRating} />
          </div>
        </div>
      )}

      {!metaReady && (
        <p className="text-[11px] text-amber-400/90 mt-3">
          <AlertCircle className="inline h-3 w-3 mr-1 -mt-0.5" />
          Enter or auto-fill a Song Title to enable Sync.
        </p>
      )}
    </SectionCard>
  );
}

function SectionCard({
  title, subtitle, children, className,
}: { title?: string; subtitle?: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-2xl border border-border bg-card/70 backdrop-blur-sm p-4 sm:p-5", className)}>
      {title && (
        <div className="mb-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

/* ---------- Drive folder picker ---------- */
type DriveFolder = { id: string; name: string; path?: string };

function useDriveFolders(_apiUrl: string) {
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // #241 — Read Drive folders from the Cloudflare endpoint, NOT from
  // the Fly `/drive/folders` stub (which always returned []). The CF
  // endpoint shares the user's Drive grant via fetchWithDriveAuth so
  // any browser with a valid session gets the real folder list with
  // no manual ID paste required. On a fresh user it auto-creates the
  // "Learning Library" parent and returns that as the lone option.
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/songs/drive-folders`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      const data = (await res.json().catch(() => ({}))) as {
        folders?: DriveFolder[];
        error?: string;
        detail?: string;
      };
      if (!res.ok) {
        const friendly = data?.detail || data?.error || `${res.status} ${res.statusText}`;
        throw new Error(friendly);
      }
      setFolders(Array.isArray(data.folders) ? data.folders : []);
    } catch (e: any) {
      setError(e?.message ?? "Could not load Drive folders");
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  return { folders, loading, error, reload: load };
}

function DriveFolderPicker({
  apiUrl, value, onChange,
}: { apiUrl: string; value: DriveFolder | null; onChange: (f: DriveFolder | null) => void }) {
  const { folders, loading, error, reload } = useDriveFolders(apiUrl);
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);

  const select = (f: DriveFolder | null) => {
    onChange(f);
    try {
      if (f) localStorage.setItem("drive_folder", JSON.stringify(f));
      else localStorage.removeItem("drive_folder");
    } catch {}
  };

  // #242 — Auto-select Learning Library on first load. User: "It
  // should automatically go to Learning Library. It should be
  // created" — the backend already auto-CREATES the folder (see
  // findOrCreateFolder in functions/api/songs/drive-folders.ts);
  // this picks it on the client too so the Sync button is enabled
  // without a tap. Precedence:
  //
  //   1. Existing `value` prop (parent already restored a default
  //      from sticky storage via getDefaultDriveFolder).
  //   2. localStorage "drive_folder" (this picker's own key from
  //      a previous session).
  //   3. The first folder returned from /api/songs/drive-folders —
  //      always Learning Library by construction (the root the
  //      backend ensures exists).
  useEffect(() => {
    if (value) return;
    // Try the picker's localStorage key first (preserves backwards
    // compat with the old behaviour where the picker remembered
    // its last selection separately).
    try {
      const raw = localStorage.getItem("drive_folder");
      if (raw) {
        const parsed = JSON.parse(raw) as DriveFolder;
        if (parsed?.id) {
          onChange(parsed);
          return;
        }
      }
    } catch { /* ignore */ }
    // Otherwise, once folders load, pick the first one (Learning
    // Library) and persist it as the sticky default.
    if (folders.length > 0) {
      select(folders[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, value]);

  return (
    <SectionCard
      title="Destination Folder"
      subtitle="Choose where on Google Drive the song bundle lands."
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          <FolderTree className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="truncate">
            {value ? (
              <>
                Saving to{" "}
                <span className="text-foreground font-medium">{value.name}</span>
                {value.path && <span className="text-muted-foreground/80"> · {value.path}</span>}
              </>
            ) : (
              <>No folder selected — pick one below.</>
            )}
          </span>
        </div>
        <button
          onClick={() => void reload()}
          className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground border border-border rounded-md px-2 py-1 shrink-0"
          disabled={loading}
        >
          <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
        </button>
      </div>

      {/* #227 — Only show the "couldn't load folders" alarm when the
          user has NO folder selected. If they've already got a sticky
          default ("Learning Library"), the upload will work fine —
          there's nothing scary to warn them about; we just can't offer
          alternative folders to browse, which is a soft failure. */}
      {error && !value && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 text-destructive p-2.5 text-[11px]">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Couldn't load folders ({error}). Paste a Drive folder ID
            manually below to continue.
          </span>
        </div>
      )}

      {folders.length > 0 && (
        <ul className="max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border bg-background/30">
          {folders.map((f) => {
            const active = value?.id === f.id;
            return (
              <li key={f.id}>
                <button
                  onClick={() => select(f)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-primary/5",
                    active && "bg-primary/10 text-primary"
                  )}
                >
                  <FolderTree className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="flex-1 min-w-0 truncate">{f.name}</span>
                  {f.path && (
                    <span className="text-[10px] text-muted-foreground truncate max-w-[40%]">{f.path}</span>
                  )}
                  {active && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && !error && folders.length === 0 && (
        <EmptyHint>No folders returned. Use a manual folder ID below.</EmptyHint>
      )}

      <button
        onClick={() => setShowManual((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronRight className={cn("h-3 w-3 transition", showManual && "rotate-90")} />
        Enter folder ID manually
      </button>

      {showManual && (
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz_drive_folder_id"
            className="bg-background/40 font-mono text-xs"
          />
          <Button
            size="sm"
            variant="outline"
            className="gap-1 border-border"
            onClick={() => {
              const id = manual.trim();
              if (!id) return toast.error("Paste a folder ID");
              select({ id, name: `Folder ${id.slice(0, 6)}…`, path: "manual" });
              setManual("");
              setShowManual(false);
              toast.success("Destination folder set");
            }}
          >
            <FolderPlus className="h-3.5 w-3.5" /> Use this ID
          </Button>
        </div>
      )}
    </SectionCard>
  );
}

/* ---------- Drive export stepper ---------- */
const ARTIFACT_ICONS: Record<DriveArtifactId, React.ComponentType<{ className?: string }>> = {
  audio_mp3: FileAudio,
  lyrics: FileText,
  translation: Languages,
  practice_sheet: FileMusic,
};

function DriveExportStepper({
  steps, folderName, compact, onRetry, onRetryOne, retrying,
}: {
  steps: DriveArtifact[];
  folderName?: string;
  compact?: boolean;
  onRetry?: (ids: DriveArtifactId[]) => void;
  onRetryOne?: (id: DriveArtifactId) => void;
  retrying?: boolean;
}) {
  const failedIds = steps.filter((s) => s.status === "error").map((s) => s.id);
  const total = steps.length;
  const done = steps.filter((s) => s.status === "done").length;
  const failed = steps.some((s) => s.status === "error");
  const running = steps.some((s) => s.status === "running");
  const pct = Math.round((done / total) * 100);

  return (
    <div className={cn("rounded-2xl border bg-card/70 backdrop-blur-sm",
      failed ? "border-destructive/30" : running ? "border-primary/30" : done === total ? "border-primary/40" : "border-border",
      compact ? "p-3" : "p-4 sm:p-5"
    )}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <CloudUpload className="h-4 w-4 text-primary shrink-0" />
            <h3 className="text-sm font-semibold truncate">Drive Export</h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
              {done}/{total}
            </span>
          </div>
          {folderName && (
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
              → <span className="text-foreground/80">{folderName}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {failed ? (
            <span className="text-[10px] uppercase tracking-wider text-destructive">Partial</span>
          ) : done === total ? (
            <span className="text-[10px] uppercase tracking-wider text-primary">Complete</span>
          ) : running || retrying ? (
            <span className="text-[10px] uppercase tracking-wider text-primary">Uploading</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Idle</span>
          )}
          {onRetry && !running && (
            <>
              {failedIds.length === 0 && (
                <span className="text-[10px] text-muted-foreground italic">
                  No failed artifacts to retry
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={retrying || failedIds.length === 0}
                onClick={() => failedIds.length > 0 && onRetry(failedIds)}
                title={failedIds.length === 0 ? "No failed artifacts to retry" : `Retry ${failedIds.length} failed artifact${failedIds.length > 1 ? "s" : ""}`}
                className={cn(
                  "h-7 px-2.5 gap-1.5",
                  failedIds.length > 0
                    ? "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    : "border-border text-muted-foreground",
                )}
              >
                {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                <span className="text-[11px]">Retry{failedIds.length > 0 ? ` ${failedIds.length}` : ""}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full rounded-full bg-muted overflow-hidden mb-3">
        <div
          className={cn("h-full transition-all", failed && !retrying ? "bg-destructive" : "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>

      <ul className="space-y-1.5">
        {steps.map((s) => {
          const Icon = ARTIFACT_ICONS[s.id];
          return (
            <li
              key={s.id}
              className={cn(
                "flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition border border-transparent",
                s.status === "running" && "bg-primary/5 border-primary/20",
                s.status === "done" && "bg-primary/[0.04]",
                s.status === "error" && "bg-destructive/10 border-destructive/30",
              )}
            >
              <span className={cn(
                "h-7 w-7 rounded-md grid place-items-center shrink-0 border",
                s.status === "done" && "bg-primary/15 border-primary/40 text-primary",
                s.status === "running" && "border-primary text-primary",
                s.status === "error" && "bg-destructive/15 border-destructive/40 text-destructive",
                s.status === "pending" && "border-border text-muted-foreground/70",
              )}>
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="flex-1 min-w-0">
                <div className={cn(
                  "text-sm leading-tight",
                  s.status === "error" ? "text-destructive" : "text-foreground",
                )}>
                  {s.label}
                </div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {s.message ?? s.hint}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {s.url && s.status === "done" && (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5"
                    title="Open on Drive"
                  >
                    Open <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
                {s.status === "error" && onRetryOne && (
                  <button
                    onClick={() => onRetryOne(s.id)}
                    disabled={retrying}
                    title={`Retry ${s.label}`}
                    className="h-6 w-6 rounded-md grid place-items-center text-destructive border border-destructive/30 hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={cn("h-3 w-3", retrying && "animate-spin")} />
                  </button>
                )}
                <span className={cn(
                  "h-5 w-5 rounded-full grid place-items-center text-[10px]",
                  s.status === "done" && "text-primary",
                  s.status === "running" && "text-primary",
                  s.status === "error" && "text-destructive",
                  s.status === "pending" && "text-muted-foreground/60",
                )}>
                  {s.status === "done" ? <Check className="h-3.5 w-3.5" /> :
                   s.status === "running" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                   s.status === "error" ? <X className="h-3.5 w-3.5" /> :
                   <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function applyDriveEvent(prev: DriveArtifact[], evt: any): DriveArtifact[] {
  const id = evt?.drive_artifact as DriveArtifactId | undefined;
  if (!id) return prev;
  return prev.map((s) =>
    s.id === id
      ? { ...s, status: (evt.status as StepStatus) ?? s.status, message: evt.message ?? s.message, url: evt.url ?? s.url }
      : s
  );
}

/**
 * Retry just the given failed Drive artifacts via the FastAPI retry endpoint.
 * Successful artifacts are preserved — only `ids` are re-uploaded.
 * Backend contract: POST {apiUrl}/drive/retry → NDJSON stream of `{drive_artifact, status, message?, url?}` events.
 */
async function retryDriveArtifacts(opts: {
  apiUrl: string;
  ids: DriveArtifactId[];
  context: Record<string, unknown>;
  onEvent: (evt: any) => void;
}) {
  const res = await fetch(`${opts.apiUrl}/drive/retry`, {
    method: "POST",
    headers: apiHeaders({
      "Content-Type": "application/json",
      Accept: "application/x-ndjson, text/plain",
    }),
    body: JSON.stringify({ artifacts: opts.ids, ...opts.context }),
  });
  if (!res.ok || !res.body) throw new Error(`FastAPI ${res.status} ${res.statusText}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      let line = buf.slice(0, nl).trim(); buf = buf.slice(nl + 1);
      if (!line) continue;
      if (line.startsWith("data:")) line = line.slice(5).trim();
      try { opts.onEvent(JSON.parse(line)); } catch { /* ignore keepalive */ }
    }
  }
}




/* ---------- Tab 1: YouTube ---------- */
function YouTubeTab({ apiUrl }: { apiUrl: string }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  // Most recent URL we've fetched oEmbed metadata for. Used as a guard
  // so a single paste doesn't trigger N fetches as the user types.
  const lastOEmbedUrlRef = useRef<string>("");
  // #232 — When the user explicitly picks a language, we don't want
  // a subsequent oEmbed run (e.g. they edit the URL slightly, or
  // we re-fire after a typo correction) to overwrite that choice
  // with the auto-detected value. Flipping this ref to true on any
  // dropdown change locks the language until the form resets.
  const lastUserPickedLanguageRef = useRef<boolean>(false);
  // Metadata is the user's entered Title/Language/Composer/etc. for the
  // song. We render the form ALWAYS (used to be gated behind a fake
  // "Ingest" button that auto-stamped placeholders — that's the bug that
  // produced "Untitled Composition · Sanskrit · Traditional" on every
  // run regardless of the actual track).
  const [meta, setMeta] = useState<SongMeta>(EMPTY_META);
  const [rating, setRating] = useState(0);
  // #272 — Single-select script picker (user: "script/lyrics should be
  // in one language only"). Default to IAST since that reads in any
  // script-agnostic browser. Both tabs share this default.
  const [scripts, setScripts] = useState<Script[]>(["English (IAST)"]);
  // Pre-seed target + drive folder from the user's saved defaults
  // (Settings → Song Workspace defaults). Both still individually
  // overridable in this tab. Lazy init = read once on mount; later
  // localStorage edits only apply on next page open which is fine
  // because the user explicitly saved + would expect the workspace to
  // reflect on reload.
  const [target, setTarget] = useState<Target>(() => getDefaultTarget() ?? "english");
  const [driveFolder, setDriveFolder] = useState<DriveFolder | null>(
    () => getDefaultDriveFolder(),
  );

  const [running, setRunning] = useState(false);
  const [statuses, setStatuses] = useState<StepStatus[]>(() => YT_STEPS.map(() => "pending"));
  const [labels, setLabels] = useState<string[]>(YT_STEPS);
  const [driveSteps, setDriveSteps] = useState<DriveArtifact[]>(blankDriveSteps);
  const [driveDone, setDriveDone] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [retrying, setRetrying] = useState(false);
  // job_id is emitted as the first NDJSON line. We need it to fetch
  // the generated artifacts (audio MP3, lyrics, translation, practice
  // sheet) from /ingest/jobs/{id}/artifact/{name} after the pipeline
  // finishes (or partially-fails — partial artifacts still render).
  const [jobId, setJobId] = useState<string | null>(null);
  // Sticky error message from the most recent run, so partial failures
  // stay visible alongside whatever artifacts did get generated.
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  // Queue-mode state: id of the youtube_queue row we're polling, plus
  // a human-readable status line shown under the stepper while we wait
  // for the Mac worker. Architecture: we hand the URL off to Fly's
  // /youtube-queue, the user's Mac daemon picks it up within ~10s,
  // downloads with residential-IP yt-dlp, and POSTs the MP3 back —
  // see backend/MAC_WORKER_INSTALL.md.
  const [queueId, setQueueId] = useState<number | null>(null);
  const [queueStatus, setQueueStatus] = useState<string | null>(null);
  // Session — we need the signed-in user's email to scope queue rows.
  const session = useSession();
  const authEmail = session.data?.email ?? null;
  // Viewport — YouTube ingest needs the user's Mac daemon, so on a
  // phone we replace the URL bar + sync controls with a "this lives
  // on your laptop" card. Toolbox and Voice Drop stay phone-native.
  const isMobile = useIsMobile();

  // Auto-fill the title from YouTube oEmbed when the user pastes a URL.
  // #230 — Goes through our same-origin /api/youtube-meta Cloudflare
  // proxy now instead of fetching youtube.com/oembed directly. The
  // direct fetch was silently failing for some users (iOS Safari ITP /
  // PWA contexts blocking third-party requests with no error), which
  // they experienced as "All the metadata was empty after i pasted
  // youtube link". The proxy is same-origin so no CORS/ITP risk, and
  // it's edge-cached for 1h so it costs nothing for repeat songs.
  // Debounce dropped to 300ms (from 600ms) — the user shouldn't have
  // to wait noticeably for the title to appear.
  const [titleFetching, setTitleFetching] = useState(false);
  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed || !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(trimmed)) {
      setTitleFetching(false);
      return;
    }
    if (lastOEmbedUrlRef.current === trimmed) return;
    setTitleFetching(true);
    const timer = setTimeout(async () => {
      lastOEmbedUrlRef.current = trimmed;
      try {
        const res = await fetch(
          `/api/youtube-meta?url=${encodeURIComponent(trimmed)}`,
        );
        if (!res.ok) return;
        const data = (await res.json()) as {
          title?: string;
          author_name?: string;
        };
        // #232 — Detect language from the title + author channel name.
        // Only override the language dropdown if the user hasn't
        // already manually changed it away from the EMPTY_META default
        // ("Sanskrit"). Tracked via lastUserPickedLanguageRef.
        const detected = detectLanguageFromTitle(
          data.title || "",
          data.author_name,
        );
        setMeta((prev) => ({
          ...prev,
          title: prev.title.trim() ? prev.title : (data.title || ""),
          language:
            lastUserPickedLanguageRef.current
              ? prev.language
              : detected ?? prev.language,
        }));

        // #259 — Song identification via Gemini. Fire-and-forget AFTER
        // oEmbed responds so we have a clean title to pass to Gemini.
        // Gemini knows famous Karnatic kritis / bhajans / etc. and
        // returns canonical metadata (composer, raga, language hint,
        // source script) we can pre-fill the form with. The user
        // pastes a YouTube URL → form auto-fills with everything.
        // No need to wait for this; if it succeeds, the fields just
        // appear after a moment. Best-effort; silent on failure.
        try {
          const ident = await fetch(`${apiUrl}/songs/identify`, {
            method: "POST",
            headers: apiHeaders({
              "content-type": "application/json",
              accept: "application/json",
            }),
            body: JSON.stringify({
              url: trimmed,
              title: data.title || "",
            }),
          });
          if (ident.ok) {
            const ig = (await ident.json()) as {
              recognized?: boolean;
              title?: string;
              composer?: string;
              language?: string;
              raga?: string;
              source_script?: string;
              confidence?: string;
            };
            if (ig.recognized) {
              // Only OVERWRITE empty fields — don't clobber anything
              // the user typed manually. Composer/raga/language fill
              // when blank; title only fills if Gemini returned a
              // canonical title AND the YouTube title is generic.
              setMeta((prev) => ({
                ...prev,
                // Composer / raga are almost always blank at this
                // stage; safe to fill from Gemini.
                composer: prev.composer.trim() ? prev.composer : (ig.composer || ""),
                raga:     prev.raga.trim()     ? prev.raga     : (ig.raga || ""),
                // Language: same guard as the oEmbed fallback — don't
                // override if user has already picked manually.
                language: lastUserPickedLanguageRef.current
                  ? prev.language
                  : (ig.language as SongLanguage | undefined) ?? prev.language,
              }));
              toast.success(
                `Recognized: ${ig.title || data.title || "this song"}`,
                {
                  description:
                    [ig.composer, ig.language, ig.raga && `Raga ${ig.raga}`]
                      .filter(Boolean)
                      .join(" · ") || "metadata auto-filled",
                  duration: 3500,
                },
              );
            }
          }
        } catch {
          /* network blip — fine, user can fill manually */
        }
      } catch {
        /* network blip — silent best-effort, user can type manually */
      } finally {
        setTitleFetching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      setTitleFetching(false);
    };
  }, [url]);

  // Sticky Drive folder (#224) — persist the user's pick so every new
  // song defaults to the last folder they used. Lazy init reads
  // Settings; this effect writes-back whenever the user changes it.
  const handleDriveFolderChange = (f: DriveFolder | null) => {
    setDriveFolder(f);
    setDefaultDriveFolder(f);
  };

  const retryFailed = async (ids: DriveArtifactId[]) => {
    if (!ids.length || retrying) return;
    if (!driveFolder) return toast.error("Pick a Google Drive destination folder");
    setRetrying(true);
    setDriveSteps((p) =>
      p.map((s) => (ids.includes(s.id) ? { ...s, status: "running", message: "Retrying upload…" } : s))
    );
    try {
      await retryDriveArtifacts({
        apiUrl,
        ids,
        context: {
          source: "youtube",
          url: url.trim(),
          meta, scripts, target,
          drive_folder_id: driveFolder.id,
          drive_folder_name: driveFolder.name,
        },
        onEvent: (evt) => {
          if (evt?.drive_artifact && ids.includes(evt.drive_artifact)) {
            setDriveSteps((p) => applyDriveEvent(p, evt));
          }
        },
      });
      setDriveSteps((p) =>
        p.map((s) =>
          ids.includes(s.id) && (s.status === "running" || s.status === "pending")
            ? { ...s, status: "done", message: "Re-uploaded" }
            : s,
        ),
      );
      toast.success("Retry complete", { description: `${ids.length} artifact${ids.length > 1 ? "s" : ""} re-uploaded.` });
    } catch (err: any) {
      setDriveSteps((p) =>
        p.map((s) =>
          ids.includes(s.id) && s.status !== "done"
            ? { ...s, status: "error", message: err?.message ?? "Retry failed" }
            : s,
        ),
      );
      toast.error("Retry failed", { description: err?.message ?? "Check your FastAPI server." });
    } finally {
      setRetrying(false);
    }
  };


  const toggleScript = (s: Script) =>
    // #272 — Radio-replace, not multi-toggle. Always exactly one script.
    setScripts([s]);

  // Whether the user has entered enough metadata to actually run the
  // pipeline. Title is the only hard requirement — it drives the Drive
  // filename + subfolder name. Everything else is graceful-degrade.
  const metaReady = !!meta.title.trim();

  const updateStep = (i: number, patch: { status?: StepStatus; message?: string }) => {
    if (patch.status) setStatuses((p) => { const n = [...p]; n[i] = patch.status!; return n; });
    if (patch.message) setLabels((p) => { const n = [...p]; n[i] = patch.message!; return n; });
  };

  /* Background submission (#224).
   *
   * Old flow blocked the user with a full-screen StepOverlay polling
   * the queue row for ~3-5 minutes while whisper.cpp transcribed the
   * MP3. The user couldn't go anywhere or do anything. New flow:
   *
   *   1. POST /youtube-queue to enqueue
   *   2. Toast "Queued — track in Songs Library"
   *   3. Navigate to /library/songs (which already auto-polls + shows
   *      every song with live status badges)
   *   4. Form resets so the next paste-and-submit is one tap away
   *
   * The user can keep submitting more URLs from there or wait. The
   * pipeline still produces the same artifacts; only the WAITING UX
   * changed.
   */
  const runSync = async () => {
    if (!url.trim()) return toast.error("Paste a YouTube URL first");
    if (!meta.title.trim()) return toast.error("Song Title is required — fill the metadata blueprint first");
    if (scripts.length === 0) return toast.error("Pick at least one script");
    if (!driveFolder) return toast.error("Pick a Google Drive destination folder");
    if (!authEmail) return toast.error("Please sign in first — the queue needs your email to route the Mac worker.");

    setRunning(true);
    try {
      const enqRes = await fetch(`${apiUrl}/youtube-queue`, {
        method: "POST",
        headers: apiHeaders({
          "Content-Type": "application/json",
          Accept: "application/json",
        }),
        body: JSON.stringify({
          url: url.trim(),
          user_email: authEmail,
          scripts, target,
          sink: "local",
          drive_folder_id: driveFolder.id,
          drive_folder_name: driveFolder.name,
          artifacts: ["audio_mp3", "lyrics", "translation", "practice_sheet"],
          meta: {
            ...meta,
            language_iso: SONG_LANGUAGES.find((l) => l.label === meta.language)?.iso ?? "",
            rating,
          },
        }),
      });
      if (!enqRes.ok) {
        const txt = await enqRes.text().catch(() => "");
        throw new Error(`Enqueue failed: ${enqRes.status} ${enqRes.statusText} ${txt}`.trim());
      }
      const enqRow = await enqRes.json();
      toast.success(`Queued #${enqRow.id} — ${meta.title}`, {
        description: "Track progress in the Songs Library. Submit another in the meantime.",
        action: { label: "Open library", onClick: () => navigate({ to: "/library/songs" }) },
        duration: 6000,
      });
      // Reset URL + metadata so the next song is one paste away. Keep
      // language/composer/etc. blank-by-default since each song has its
      // own metadata, BUT keep the Drive folder + rating + scripts as
      // the user's current session preferences.
      setUrl("");
      setMeta(EMPTY_META);
      setRating(0);
      lastOEmbedUrlRef.current = "";
      lastUserPickedLanguageRef.current = false;
      // Navigate to the library so the user can see the new song in
      // context with everything else. Songs Library auto-polls so the
      // status will flip from queued → claimed → uploaded → done in
      // real time as the daemon + pipeline make progress.
      navigate({ to: "/library/songs" });
    } catch (err: any) {
      toast.error("Couldn't queue the song", {
        description: err?.message ?? "Check your FastAPI server.",
      });
    } finally {
      setRunning(false);
    }
  };

  // Mobile guard — YouTube ingest depends on the user's Mac running the
  // launchd daemon (see backend/MAC_WORKER_INSTALL.md). On a phone there's
  // no daemon to fall back to, so showing the input would just lead to
  // rows that sit in 'pending' forever. Instead show the explanation +
  // a deep link into the Voice Drop tab which IS phone-native.
  if (isMobile) {
    return (
      <div className="grid gap-6">
        <SectionCard className="border-amber-500/40 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-base mb-1">YouTube ingest lives on your laptop</div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                YouTube blocks our cloud server from downloading audio. The fix
                is a tiny daemon on your Mac that does the download from a
                residential IP and ships the MP3 back. That daemon only runs
                on macOS — so the YouTube tab is desktop-only.
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                On your phone, switch to <span className="text-primary font-medium">Voice Drop</span>
                {" "}— share an audio file from WhatsApp or any other app, or
                record a quick hum, and the same pipeline (transcribe →
                transliterate → translate → Drive) takes over.
              </p>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="The inaugural song" subtitle="Save this link for your laptop session.">
          <a
            href={INAUGURAL_YOUTUBE}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs text-primary break-all underline underline-offset-2"
          >
            {INAUGURAL_YOUTUBE}
          </a>
          <p className="text-[11px] text-muted-foreground mt-2">
            This is the first track Uma wants the Mac-worker pipeline to handle.
            Open this page on your laptop and tap <span className="text-primary">Try the inaugural song</span> to seed it.
          </p>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      {/* URL bar — no fake "Ingest" button (that was the source of the
          auto-filled wrong metadata). The user pastes here, fills the
          metadata form below, then taps Sync at the bottom. */}
      <SectionCard>
        <label className="text-xs uppercase tracking-wider text-muted-foreground">YouTube URL</label>
        <div className="relative mt-2">
          <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary" />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=…"
            className="pl-9 h-11 bg-background/60 border-border"
          />
        </div>
        {/* One-tap pill: prefill the URL with the inaugural test song.
            Only shown when the input is empty so it doesn't compete with a
            user who's already pasted a different URL. See INAUGURAL_YOUTUBE. */}
        {!url.trim() && (
          <button
            type="button"
            onClick={() => setUrl(INAUGURAL_YOUTUBE)}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-primary hover:text-primary/80 border border-primary/30 hover:border-primary/60 rounded-full px-3 py-1 transition"
          >
            <Sparkles className="h-3 w-3" /> Try the inaugural song
          </button>
        )}
      </SectionCard>

      {/* #227 — Metadata simplification.
          User feedback: "why do have metadata like song title, date etc.
          can you revisit on the metadata" + "language should appear as
          sanskrit instead of autodetect".
          Only Title (required) + Language (drives whisper accuracy) stay
          on-screen by default. Composer / Raga / Album / Year / Curate
          drop into an optional "Add more details" disclosure — they're
          useful for Drive filenames + ID3 tags but never block the
          pipeline, so most users never need to fill them. Title is
          auto-filled from YouTube oEmbed (see the URL-change effect). */}
      <SongMetadataCard
        meta={meta}
        setMeta={setMeta}
        rating={rating}
        setRating={setRating}
        metaReady={metaReady}
        titleFetching={titleFetching}
        onLanguagePick={() => {
          lastUserPickedLanguageRef.current = true;
        }}
      />

      {/* Scripts + Target */}
      <div className="grid md:grid-cols-2 gap-6">
        <SectionCard title="Script & Lyrics" subtitle="Pick the script for lyrics — one language only.">
          <ScriptChips scripts={scripts} onToggle={toggleScript} />
        </SectionCard>
        <SectionCard title="Target Translation" subtitle="Choose the language for the rendered translation.">
          <TargetSegmented target={target} onChange={setTarget} />
        </SectionCard>
      </div>

      <DriveFolderPicker apiUrl={apiUrl} value={driveFolder} onChange={handleDriveFolderChange} />

      {/* Action panel */}
      <SectionCard className="bg-gradient-to-br from-card/80 to-primary/5 border-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div>
            <h3 className="font-display text-lg">Sync & Save</h3>
            <p className="text-xs text-muted-foreground">
              {driveFolder
                ? <>Bundles MP3, lyrics & translation into <span className="text-primary">{driveFolder.name}</span> on Drive.</>
                : <>Pick a destination folder above to enable upload.</>}
            </p>
          </div>
          <Button
            onClick={runSync}
            disabled={running || !metaReady || !url.trim() || !driveFolder}
            className="h-11 px-5 gap-2 shadow-[0_0_24px_-6px_oklch(0.72_0.14_162/0.6)]"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
            {running ? "Syncing…" : "Sync & Save to Google Drive"}
          </Button>
        </div>
      </SectionCard>

      {/* Sticky pipeline error banner — shown when the backend
          emitted an error event mid-stream (e.g. yt-dlp bot wall).
          Replaces the silent "fake success" v207 produced. */}
      {pipelineError && !running && (
        <SectionCard className="border-destructive/40 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-destructive mb-1">Pipeline failed</div>
              <pre className="text-[11px] text-destructive/90 whitespace-pre-wrap break-words font-mono leading-relaxed max-h-32 overflow-auto">
                {pipelineError}
              </pre>
              {/yt-dlp|youtube|bot/i.test(pipelineError) && (
                <p className="text-[11px] text-destructive/80 mt-2">
                  YouTube blocked the download. If the error came from your Mac
                  worker, retry in a few minutes (residential IP rate-limit) or
                  refresh yt-dlp: <span className="font-mono">brew upgrade yt-dlp</span>.
                  See <span className="font-mono">backend/MAC_WORKER_INSTALL.md</span>.
                </p>
              )}
              {/Mac worker|daemon|queue/i.test(pipelineError) && (
                <p className="text-[11px] text-destructive/80 mt-2">
                  Your Mac worker may be offline. Check
                  <span className="font-mono"> launchctl list | grep ojaswati</span>
                  — if missing, see <span className="font-mono">backend/MAC_WORKER_INSTALL.md</span>.
                </p>
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {/* Persistent post-sync Drive export card */}
      {(driveDone || driveSteps.some((s) => s.status !== "pending")) && !running && (
        <DriveExportStepper
          steps={driveSteps}
          folderName={driveFolder?.name}
          onRetry={retryFailed}
          onRetryOne={(id) => retryFailed([id])}
          retrying={retrying}
        />
      )}

      {/* Generated artifacts panel — only shown once we have a
          job_id from the stream. Renders audio player + lyrics +
          translation + practice sheet download even when the
          pipeline partially failed. Each artifact handles its own
          404/missing state. */}
      {jobId && !running && (
        <ArtifactPanel
          apiUrl={apiUrl}
          jobId={jobId}
          title="Listen & review"
          driveFolderId={driveFolder?.id}
          songTitle={meta.title}
          songMeta={meta}
        />
      )}

      {/* #230 — StepOverlay deleted. It used to take over the screen
          for the full ~3-5 min pipeline run, which the user described
          as "happening in foreground and I am not able to transition."
          Submit is genuinely fire-and-forget now: runSync POSTs to the
          queue (~200ms), shows a toast, and navigates to /library/songs
          where the new row appears at the top auto-refreshing every 5s.
          All status display lives on Songs Library — there's no longer
          any in-workspace progress UI to fight with the navigation. */}
    </div>
  );
}

/* ---------- Tab 2: WhatsApp ---------- */
function WhatsAppTab({ apiUrl }: { apiUrl: string }) {
  const [file, setFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // #272 — Single-select script picker (user: "script/lyrics should be
  // in one language only"). Default to IAST since that reads in any
  // script-agnostic browser. Both tabs share this default.
  const [scripts, setScripts] = useState<Script[]>(["English (IAST)"]);
  // Pre-seed target + drive folder from the user's saved defaults
  // (Settings → Song Workspace defaults). See same pattern in YouTubeTab.
  const [target, setTarget] = useState<Target>(() => getDefaultTarget() ?? "english");
  const [processing, setProcessing] = useState(false);
  const [driveFolder, setDriveFolder] = useState<DriveFolder | null>(
    () => getDefaultDriveFolder(),
  );
  const [driveSteps, setDriveSteps] = useState<DriveArtifact[]>(() =>
    blankDriveSteps().filter((s) => s.id !== "practice_sheet")
  );
  const [showStepper, setShowStepper] = useState(false);
  // job_id from the stream's first event + sticky pipeline error
  // message. See YouTubeTab for the rationale (#209).
  const [jobId, setJobId] = useState<string | null>(null);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const acceptFile = (f: File | null | undefined) => {
    if (!f) return;
    if (!f.type.startsWith("audio/") && !/\.(opus|m4a|mp3|wav|ogg|aac)$/i.test(f.name)) {
      return toast.error("That doesn't look like an audio file");
    }
    setFile(f);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    toast.success("Audio staged", { description: `${f.name} · ${(f.size / 1_048_576).toFixed(2)} MB` });
  };

  // Web Share Target intake — when launched from WhatsApp share sheet (?shared=1),
  // pull the file the service worker stashed for us.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("shared") !== "1") return;
    (async () => {
      try {
        const res = await fetch("/__shared-audio__");
        if (!res.ok) return;
        const blob = await res.blob();
        const filename = decodeURIComponent(res.headers.get("X-Filename") || "shared-audio.opus");
        acceptFile(new File([blob], filename, { type: blob.type || "audio/ogg" }));
        // clean the URL so a reload doesn't re-import
        window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up object URL on unmount
  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const retryFailed = async (ids: DriveArtifactId[]) => {
    if (!ids.length || retrying) return;
    if (!driveFolder) return toast.error("Pick a Google Drive destination folder");
    if (!file) return toast.error("Pick an audio file first");
    setRetrying(true);
    setDriveSteps((p) =>
      p.map((s) => (ids.includes(s.id) ? { ...s, status: "running", message: "Retrying upload…" } : s))
    );
    try {
      await retryDriveArtifacts({
        apiUrl,
        ids,
        context: {
          source: "whatsapp",
          filename: file.name,
          scripts, target,
          drive_folder_id: driveFolder.id,
          drive_folder_name: driveFolder.name,
        },
        onEvent: (evt) => {
          if (evt?.drive_artifact && ids.includes(evt.drive_artifact)) {
            setDriveSteps((p) => applyDriveEvent(p, evt));
          }
        },
      });
      setDriveSteps((p) =>
        p.map((s) =>
          ids.includes(s.id) && (s.status === "running" || s.status === "pending")
            ? { ...s, status: "done", message: "Re-uploaded" }
            : s,
        ),
      );
      toast.success("Retry complete", { description: `${ids.length} artifact${ids.length > 1 ? "s" : ""} re-uploaded.` });
    } catch (err: any) {
      setDriveSteps((p) =>
        p.map((s) =>
          ids.includes(s.id) && s.status !== "done"
            ? { ...s, status: "error", message: err?.message ?? "Retry failed" }
            : s,
        ),
      );
      toast.error("Retry failed", { description: err?.message ?? "Check your FastAPI server." });
    } finally {
      setRetrying(false);
    }
  };

  const toggleScript = (s: Script) =>
    // #272 — Radio-replace, not multi-toggle. Always exactly one script.
    setScripts([s]);

  const onProcess = async () => {
    if (!file) return toast.error("Pick an audio file first");
    if (scripts.length === 0) return toast.error("Pick at least one script");
    if (!driveFolder) return toast.error("Pick a Google Drive destination folder");
    setProcessing(true);
    setShowStepper(true);
    setDriveSteps(blankDriveSteps().filter((s) => s.id !== "practice_sheet"));
    setJobId(null);
    setPipelineError(null);
    try {
      const fd = new FormData();
      fd.append("audio", file, file.name);
      fd.append("scripts", JSON.stringify(scripts));
      fd.append("target", target);
      // Same architecture as YouTubeTab — Fly stores locally, browser
      // uploads to Drive via Cloudflare /api/songs/upload-to-drive.
      fd.append("sink", "local");
      fd.append("drive_folder_id", driveFolder.id);
      fd.append("drive_folder_name", driveFolder.name);
      fd.append("artifacts", JSON.stringify(["audio_mp3", "lyrics", "translation"]));

      const res = await fetch(`${apiUrl}/whatsapp`, {
        method: "POST",
        // Don't set Content-Type — letting the browser pick it ensures
        // the multipart boundary header is correctly generated.
        headers: apiHeaders({ Accept: "application/x-ndjson, text/plain" }),
        body: fd,
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);

      let streamError: string | null = null;
      if (res.body && res.headers.get("content-type")?.includes("ndjson")) {
        const langTracker = createLangMismatchTracker(target);
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        outer: while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf("\n")) >= 0) {
            let line = buf.slice(0, nl).trim(); buf = buf.slice(nl + 1);
            if (!line) continue;
            if (line.startsWith("data:")) line = line.slice(5).trim();
            let evt: any;
            try { evt = JSON.parse(line); } catch { continue; }
            // job_id event — first line of every stream (#209).
            if (typeof evt?.job_id === "string") {
              setJobId(evt.job_id);
              continue;
            }
            // Real backend error — abort with the actual message.
            if (evt?.status === "error" && !evt?.drive_artifact) {
              streamError = evt.message || "Pipeline failed";
              break outer;
            }
            if (evt?.drive_artifact) setDriveSteps((p) => applyDriveEvent(p, evt));
            checkTranslationLang(evt, target, langTracker);
          }
        }
      } else {
        // Non-streaming JSON response — still verify translation_lang
        try {
          const json = await res.clone().json();
          checkTranslationLang(json, target, createLangMismatchTracker(target));
        } catch { /* not JSON, skip */ }
      }

      if (streamError) {
        setDriveSteps((p) =>
          p.map((s) =>
            s.status === "pending" || s.status === "running"
              ? { ...s, status: "error", message: streamError ?? undefined }
              : s,
          ),
        );
        setPipelineError(streamError);
        toast.error("Pipeline failed", { description: streamError.slice(0, 200) });
      } else {
        setDriveSteps((p) => p.map((s) => (s.status === "pending" || s.status === "running" ? { ...s, status: "done" } : s)));
        setPipelineError(null);
        toast.success("Saved to Google Drive", {
          description: `Audio + lyrics + translation pushed to ${driveFolder.name}.`,
        });
      }
    } catch (e: any) {
      setDriveSteps((p) => {
        const idx = p.findIndex((s) => s.status === "running");
        const tgt = idx >= 0 ? idx : p.findIndex((s) => s.status === "pending");
        if (tgt < 0) return p;
        const next = [...p];
        next[tgt] = { ...next[tgt], status: "error", message: e?.message ?? "Upload failed" };
        return next;
      });
      toast.error("Drive export failed", { description: e?.message ?? "Check your FastAPI server." });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl grid gap-6">
      {/* Drop zone + real file picker */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "rounded-2xl border-2 border-dashed p-8 text-center transition cursor-pointer",
          dragOver ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
        )}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-primary/15 flex items-center justify-center">
          <Smartphone className="h-6 w-6 text-primary" />
        </div>
        <div className="mt-3 font-display text-lg">Tap to pick audio · or share from any app</div>
        <p className="text-xs text-muted-foreground mt-1">
          .opus · .m4a · .mp3 · .wav — share a voice note from
          {" "}<span className="text-primary font-medium">WhatsApp</span>, Voice Memos, or Telegram and pick
          {" "}<span className="text-primary">ojaswati</span>.
        </p>
        {/* #273 — Discoverability hint: the WhatsApp share path only
            works after the user installs the PWA to their phone home
            screen (Android — iOS Safari doesn't support share targets).
            Without this hint the user can't tell why "ojaswati" isn't
            in their share sheet. */}
        <p className="text-[11px] text-muted-foreground/80 mt-1.5 italic">
          First time? Open ojaswati.com on your phone → Add to Home Screen →
          then share a voice note. "ojaswati" appears in the share sheet on Android.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-4 gap-2 border-border"
          onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
        >
          <Upload className="h-3.5 w-3.5" /> Browse files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.opus,.m4a,.mp3,.wav,.ogg"
          hidden
          onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>

      {/* #275 — WhatsApp Inbox. iPhone users can't share into a PWA via
          the share sheet (iOS Safari limitation), so the alternative
          path is: send a voice note to your registered WhatsApp Business
          number → the webhook stores it → this list polls every 5s and
          shows it → tap to stage. See docs/WHATSAPP_SETUP.md. */}
      <WhatsAppInbox onStage={acceptFile} />

      {/* MyAudio — record from the device mic and stage straight into the inbox */}
      <AudioRecorder onRecorded={acceptFile} />



      {/* Staged card */}
      {file && audioUrl ? (
        <SectionCard>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-[0_0_18px_-4px_oklch(0.72_0.14_162/0.7)]">
              <Play className="h-5 w-5 ml-0.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{file.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {(file.size / 1_048_576).toFixed(2)} MB · {file.type || "audio"}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setFile(null); if (audioUrl) URL.revokeObjectURL(audioUrl); setAudioUrl(null); }}
            >
              Clear
            </Button>
          </div>
          <audio src={audioUrl} controls className="mt-4 w-full" preload="metadata" />
          <div className="mt-4 flex flex-wrap gap-1.5">
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">
              Staged audio
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
              {file.name.split(".").pop()?.toLowerCase() || "audio"}
            </span>
          </div>
        </SectionCard>
      ) : (
        <EmptyHint>Inbox clear. Tap the drop zone above or share a voice note from any app to stage it.</EmptyHint>
      )}

      <SectionCard title="Script & Lyrics" subtitle="Pick the script for lyrics — one language only.">
        <ScriptChips scripts={scripts} onToggle={toggleScript} />
      </SectionCard>

      <SectionCard title="Target Translation">
        <TargetSegmented target={target} onChange={setTarget} />
      </SectionCard>

      <DriveFolderPicker apiUrl={apiUrl} value={driveFolder} onChange={setDriveFolder} />

      <Button onClick={onProcess} disabled={processing || !file} className="h-12 gap-2">
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />}
        Process & Save to Google Drive
      </Button>

      {pipelineError && !processing && (
        <SectionCard className="border-destructive/40 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-destructive mb-1">Pipeline failed</div>
              <pre className="text-[11px] text-destructive/90 whitespace-pre-wrap break-words font-mono leading-relaxed max-h-32 overflow-auto">
                {pipelineError}
              </pre>
            </div>
          </div>
        </SectionCard>
      )}

      {showStepper && (
        <DriveExportStepper
          steps={driveSteps}
          folderName={driveFolder?.name}
          onRetry={retryFailed}
          onRetryOne={(id) => retryFailed([id])}
          retrying={retrying}
        />
      )}

      {/* Audio + lyrics + translation viewer — see ArtifactPanel docs. */}
      {jobId && !processing && (
        <ArtifactPanel
          apiUrl={apiUrl}
          jobId={jobId}
          title="Listen & review"
          driveFolderId={driveFolder?.id}
          songTitle={file?.name?.replace(/\.[^.]+$/, "")}
        />
      )}
    </div>
  );
}

/* ---------- Tab 3: Toolbox ---------- */
function ToolboxTab() {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <AudioToolkit />
      <VideoToolkit />
      <ImageToolkit />
    </div>
  );
}

function AudioToolkit() {
  const [files, setFiles] = useState<string[]>([]);
  const [crossfade, setCrossfade] = useState([0.5]);

  return (
    <SectionCard title="Audio Toolkit" subtitle="Merge tracks · cross-fade · precision trim">
      <div className="space-y-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = Array.from(e.dataTransfer.files).map((f) => f.name);
            setFiles((p) => [...p, ...dropped]);
            if (dropped.length) toast(`Stacked ${dropped.length} file(s)`);
          }}
          className="rounded-xl border-2 border-dashed border-border p-5 text-center bg-background/30 hover:border-primary/40 transition"
        >
          <Layers className="h-5 w-5 mx-auto text-primary" />
          <div className="text-xs mt-2 text-muted-foreground">Drop audio files to stack for merging</div>
          {files.length > 0 && (
            <ul className="mt-3 text-left space-y-1">
              {files.map((f, i) => (
                <li key={i} className="text-xs flex items-center gap-2 bg-card/60 border border-border rounded-md px-2 py-1.5">
                  <span className="text-primary/80">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate">{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between text-xs">
            <label className="text-muted-foreground">Song Transition (Audio Cross-fade)</label>
            <span className="text-primary font-mono">{crossfade[0].toFixed(2)}s</span>
          </div>
          <Slider min={0} max={3} step={0.05} value={crossfade} onValueChange={setCrossfade} className="mt-2" />
        </div>

        <div className="pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground mb-2">Precision Trim — visual handles, no timestamps</div>
          <RangeTrimmer
            onApply={({ file, start, end }) =>
              toast.success("Trim queued", { description: `${file.name} · ${start.toFixed(2)}s → ${end.toFixed(2)}s` })
            }
          />
        </div>
      </div>
    </SectionCard>
  );
}

function VideoToolkit() {
  const [audio, setAudio] = useState<string | null>(null);
  const [video, setVideo] = useState<string | null>(null);
  const [profile, setProfile] = useState<"h264" | "mov" | "srt">("h264");

  const Slot = ({
    label, value, onFile, accept,
  }: { label: string; value: string | null; onFile: (n: string) => void; accept: string }) => (
    <label
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) onFile(f.name); }}
      className="block rounded-xl border-2 border-dashed border-border bg-background/30 px-4 py-6 text-center cursor-pointer hover:border-primary/40 transition"
    >
      <input type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f.name); }} />
      <Video className="h-5 w-5 mx-auto text-primary" />
      <div className="text-xs mt-2">{value ? <span className="text-foreground">{value}</span> : <span className="text-muted-foreground">{label}</span>}</div>
    </label>
  );

  const profiles = [
    { id: "h264", label: "Web-Optimized H.264 (.mp4)" },
    { id: "mov", label: "High-Fidelity Master (.mov)" },
    { id: "srt", label: "Hardcode SRT Subtitle Track" },
  ] as const;

  return (
    <SectionCard title="Video Toolkit" subtitle="Compose lyric videos from audio + visual.">
      <div className="space-y-3">
        <Slot label="[Drop Audio MP3 File]" value={audio} onFile={setAudio} accept="audio/*" />
        <Slot label="[Drop Video MP4 or Static Image]" value={video} onFile={setVideo} accept="video/*,image/*" />

        <div className="space-y-1.5 pt-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Export Profile</div>
          {profiles.map((p) => (
            <label key={p.id} className={cn(
              "flex items-center gap-2.5 rounded-lg border px-3 py-2 text-xs cursor-pointer transition",
              profile === p.id ? "border-primary/40 bg-primary/10 text-foreground" : "border-border bg-background/30 text-muted-foreground hover:text-foreground"
            )}>
              <input
                type="radio"
                name="profile"
                checked={profile === p.id}
                onChange={() => setProfile(p.id)}
                className="accent-[oklch(0.72_0.14_162)]"
              />
              {p.label}
            </label>
          ))}
        </div>

        <Button variant="outline" className="w-full border-border">Render</Button>
      </div>
    </SectionCard>
  );
}

function ImageToolkit() {
  const [src, setSrc] = useState<string | null>(null);
  const [overlay, setOverlay] = useState("");

  const onFile = (f?: File | null) => {
    if (!f) return;
    const url = URL.createObjectURL(f);
    setSrc(url);
  };

  return (
    <SectionCard title="Image Toolkit" subtitle="Square 500×500 album art with watermark layer.">
      <div className="space-y-3">
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          className="block aspect-square w-full max-w-[320px] mx-auto rounded-xl border-2 border-dashed border-border overflow-hidden relative bg-background/30 cursor-pointer hover:border-primary/40 transition"
          style={{ backgroundImage: src ? `url(${src})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          {!src && (
            <div className="absolute inset-0 grid place-items-center text-center px-4">
              <div>
                <ImageIcon className="h-6 w-6 mx-auto text-primary" />
                <div className="text-xs mt-2 text-muted-foreground">Drop or browse · snaps to 500×500</div>
                <div className="text-[10px] mt-0.5 text-muted-foreground/70">App Standard</div>
              </div>
            </div>
          )}
          {src && overlay && (
            <div className="absolute inset-0 flex items-end justify-center p-4">
              <div className="font-display text-xl text-white/90 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)] text-center leading-tight">
                {overlay}
              </div>
            </div>
          )}
        </label>

        <Field label="Watermark / Title Overlay Text">
          <Input value={overlay} onChange={(e) => setOverlay(e.target.value)} placeholder="e.g. Bhaja Govindam" className="bg-background/40" />
        </Field>

        <Button variant="outline" className="w-full border-border">Export 500×500 PNG</Button>
      </div>
    </SectionCard>
  );
}

/* ────────────────────────────────────────────────────────────────────
   ArtifactPanel — render the four pipeline outputs (audio MP3, lyrics
   text, translation text, practice sheet markdown) for a completed
   (or partially completed) job. Fetches each artifact from
   GET /ingest/jobs/{id}/artifact/{name} with the X-Ojaswati-Key header.
   404s are common when a stage failed mid-pipeline — each artifact
   handles its own missing-state.

   Auth note: the MP3 is fetched as a blob and turned into an object
   URL because <audio src=...> can't carry custom headers. The text
   files are fetched as strings and rendered inline.
   ──────────────────────────────────────────────────────────────────── */

interface ArtifactState<T> {
  status: "idle" | "loading" | "ready" | "missing" | "error";
  data?: T;
  error?: string;
}

/** Per-artifact Drive upload state. */
interface DriveUpload {
  status: "idle" | "uploading" | "done" | "failed" | "skipped";
  webViewLink?: string;
  error?: string;
}

type ArtifactName = "audio_mp3" | "lyrics" | "translation" | "practice_sheet";

async function fetchArtifact(
  apiUrl: string,
  jobId: string,
  name: ArtifactName,
): Promise<Response> {
  return fetch(`${apiUrl}/ingest/jobs/${encodeURIComponent(jobId)}/artifact/${name}`, {
    headers: apiHeaders(),
  });
}

/**
 * Sanitise a free-text string into a Drive-safe filename component.
 * Strips path separators, control chars, and trims to 80 chars to leave
 * room for the suffix without blowing Drive's 255-char file-name cap.
 * Falls back to ``fallback`` when the cleaned string is empty.
 */
function safeSlug(s: string, fallback: string): string {
  const cleaned = (s || "")
    .replace(/[\/\\:<>"|?*\x00-\x1f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return cleaned || fallback;
}

/**
 * Drive filenames for each artifact. Human-readable form so the user
 * can find them in Drive without the job-id suffix dominating. We
 * intentionally do NOT append the job_id — the per-song subfolder
 * (see driveSubfolderFor) provides the disambiguation, and Drive
 * tolerates same-named files because each lives in its own folder.
 */
function driveFilenameFor(name: ArtifactName, _jobId: string, songTitle: string): string {
  const slug = safeSlug(songTitle, "song");
  switch (name) {
    case "audio_mp3":      return `${slug}.mp3`;
    case "lyrics":         return `${slug} — lyrics.txt`;
    case "translation":    return `${slug} — translation.txt`;
    // #233 — Combined "lyrics + translation" doc: that's what the user
    // actually wants in Drive per song. Renamed from "practice sheet"
    // because the new client-built HTML contains lyrics + translation
    // side-by-side (not chord charts or practice notes).
    case "practice_sheet": return `${slug} — lyrics + translation.html`;
  }
}

/* #233 — Build a self-contained, print-friendly HTML doc containing
 * the song's lyrics + translation side-by-side. Opens cleanly in
 * Google Drive's preview, prints to PDF nicely, and survives offline.
 * All CSS is inline so the file is fully portable. */
function buildLyricsTranslationHTML(args: {
  meta?: SongMeta;
  songTitle: string;
  lyricsLines: string[];
  translationLines: string[];
}): string {
  const { meta, songTitle, lyricsLines, translationLines } = args;
  const rowCount = Math.max(lyricsLines.length, translationLines.length);
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const subtitleBits = [
    meta?.composer,
    meta?.raga,
    meta?.album,
    meta?.year,
    meta?.language,
  ].filter(Boolean);
  const rowsHtml = Array.from({ length: rowCount })
    .map((_, i) => {
      const l = esc(lyricsLines[i] ?? "");
      const t = esc(translationLines[i] ?? "");
      return `<tr><td class="num">${i + 1}</td><td class="src">${l}</td><td class="tr">${t}</td></tr>`;
    })
    .join("\n");
  return `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8">
<title>${esc(songTitle)} — lyrics + translation</title>
<style>
:root { color-scheme: light dark; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  max-width: 880px; margin: 0 auto; padding: 28px 24px 80px; line-height: 1.55;
  color: #1a1a1a; background: #fdfaf5; }
@media (prefers-color-scheme: dark) {
  body { color: #f0e9dc; background: #1a1815; }
  table { border-color: #3a342a; }
  th, td { border-color: #3a342a; }
}
h1 { font-family: Georgia, "Times New Roman", serif; font-weight: 600;
  font-size: 28px; margin: 0 0 4px; }
p.subtitle { margin: 0 0 24px; color: #777; font-size: 13px; }
table { width: 100%; border-collapse: collapse; font-size: 15px; }
th, td { border-bottom: 1px solid #e5dcc8; padding: 10px 12px; vertical-align: top; }
th { text-align: left; font-size: 11px; text-transform: uppercase;
  letter-spacing: 0.06em; color: #888; font-weight: 600; }
td.num { width: 36px; color: #999; font-variant-numeric: tabular-nums;
  font-size: 12px; }
td.src { width: 50%; font-weight: 500; }
td.tr  { width: 50%; color: #555; }
@media (prefers-color-scheme: dark) { td.tr { color: #bbb; } }
@media print { body { background: white; color: black; } }
</style>
</head><body>
<h1>${esc(songTitle)}</h1>
<p class="subtitle">${esc(subtitleBits.join(" · ") || "Ojaswati Learning Library")}</p>
<table>
<thead><tr><th>#</th><th>Lyrics</th><th>Translation</th></tr></thead>
<tbody>
${rowsHtml}
</tbody>
</table>
</body></html>`;
}

/**
 * Drive subfolder name for a song. Each song lands in its own subfolder
 * under the user's chosen parent so MP3 + lyrics + translation + practice
 * sheet stay grouped. Includes composer when known to disambiguate
 * different artists' versions of the same composition.
 */
function driveSubfolderFor(meta: SongMeta): string {
  const title = safeSlug(meta.title, "Untitled Song");
  const composer = safeSlug(meta.composer, "");
  return composer ? `${title} — ${composer}` : title;
}

/**
 * Per-artifact Drive custom properties. Drive exposes `properties` as
 * searchable key/value pairs on every file — drives the "search by
 * raga" / "search by composer" UX once the Library view ships. Values
 * are limited to 124 chars each per Drive's API rules.
 */
function driveCustomProperties(meta: SongMeta, name: ArtifactName): Record<string, string> {
  const cap = (s: string) => (s || "").slice(0, 124);
  const props: Record<string, string> = {
    "ojaswati_artifact": name,
    "ojaswati_song_title": cap(meta.title),
  };
  if (meta.language && meta.language !== "Auto-detect") props["ojaswati_language"] = cap(meta.language);
  if (meta.composer) props["ojaswati_composer"] = cap(meta.composer);
  if (meta.raga)     props["ojaswati_raga"]     = cap(meta.raga);
  if (meta.album)    props["ojaswati_album"]    = cap(meta.album);
  if (meta.year)     props["ojaswati_year"]     = cap(meta.year);
  return props;
}

/** Drive mime-type per artifact. The backend's served files match.
 * #233 — practice_sheet is now an HTML doc (lyrics + translation
 * side-by-side, client-built) instead of the server-side .md, so
 * the mime flips to text/html. Drive previews HTML inline. */
const DRIVE_MIME: Record<ArtifactName, string> = {
  audio_mp3:      "audio/mpeg",
  lyrics:         "text/plain",
  translation:    "text/plain",
  practice_sheet: "text/html; charset=utf-8",
};

/**
 * Upload a single artifact to Drive via /api/songs/upload-to-drive.
 * Returns the Drive file's webViewLink, or throws with a clean message.
 *
 * When ``subfolderName`` is provided the proxy looks for (or creates)
 * a subfolder of that name inside ``parentFolderId`` and uploads the
 * artifact there — keeps each song's files grouped on Drive.
 *
 * ``properties`` is a flat key/value map written as Drive custom
 * properties, searchable via Drive's `properties has` query — that's
 * what powers "find songs in raga Bhairavi" later.
 */
async function uploadArtifactToDrive(args: {
  blob: Blob;
  filename: string;
  mimeType: string;
  parentFolderId: string;
  subfolderName?: string;
  properties?: Record<string, string>;
}): Promise<string | null> {
  const fd = new FormData();
  fd.append("file", args.blob, args.filename);
  fd.append("filename", args.filename);
  fd.append("mime_type", args.mimeType);
  fd.append("parent_folder_id", args.parentFolderId);
  if (args.subfolderName) fd.append("subfolder_name", args.subfolderName);
  if (args.properties && Object.keys(args.properties).length > 0) {
    fd.append("properties", JSON.stringify(args.properties));
  }
  const res = await fetch("/api/songs/upload-to-drive", {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) {
    let detail = "";
    try {
      const j = (await res.json()) as { error?: string; detail?: string };
      detail = j.detail || j.error || `HTTP ${res.status}`;
    } catch {
      detail = `HTTP ${res.status}`;
    }
    throw new Error(detail.slice(0, 200));
  }
  const j = (await res.json()) as { web_view_link?: string };
  return j.web_view_link ?? null;
}

/* Exported #231 — re-used by /library/songs/$jobId so the user can
 * open a finished song's audio + lyrics + translation from the
 * Songs Library row's "open" button without re-entering the
 * workspace (which previously dumped them onto the empty Add form). */
export function ArtifactPanel({
  apiUrl, jobId, title, driveFolderId, songTitle, songMeta, youtubeUrl,
}: {
  apiUrl: string;
  jobId: string;
  title?: string;
  /** When set, each artifact is auto-uploaded to Drive after fetch. */
  driveFolderId?: string | null;
  /** Used to build human Drive filenames. */
  songTitle?: string;
  /** Full metadata. When provided, drives subfolder + custom properties
   *  on each Drive upload. Optional so older callers (WhatsAppTab) still
   *  work — they'll upload flat with just the filename derived from title. */
  songMeta?: SongMeta;
  /** #263 — When provided, ArtifactPanel auto-fetches the canonical
   *  lyrics from Gemini-grounded on mount and displays them as the
   *  primary view (instead of whisper transcription). If Gemini doesn't
   *  recognize the song, falls back to the existing whisper-based view. */
  youtubeUrl?: string;
}) {
  const [audio, setAudio] = useState<ArtifactState<string>>({ status: "idle" });
  // Ref attached to the <audio> element so the lyrics viewer (#224)
  // can subscribe to timeupdate events and drive karaoke-style verse
  // highlighting. Also used by click-to-seek on verses.
  const artifactAudioRef = useRef<HTMLAudioElement | null>(null);
  // Structured verses (text + audio start/end per verse) for karaoke.
  // Fetched from verses.json when the song was processed after #224;
  // pre-#224 jobs simply skip the karaoke highlight + click-to-seek.
  const [versesTimed, setVersesTimed] = useState<{ sequence: number; text: string; start: number; end: number; source_script: string }[]>([]);
  const [lyrics, setLyrics] = useState<ArtifactState<string>>({ status: "idle" });
  const [translation, setTranslation] = useState<ArtifactState<string>>({ status: "idle" });
  // Per-script verse arrays (#222/#223). Keys are script tags:
  // "iast", "tamil", "devanagari". Backend emits all three after #223
  // so the viewer's Lyrics dropdown always has every option.
  const [transliterations, setTransliterations] = useState<Record<string, string[]>>({});
  // Per-language verse arrays (#223). Keys are target languages:
  // "english", "tamil", "sanskrit". Powers the Translation dropdown.
  const [allTranslations, setAllTranslations] = useState<Record<string, string[]>>({});
  // Currently selected script for the Lyrics column. null = "Original"
  // (use lyrics.txt directly, in the source script the singer used).
  const [lyricsScript, setLyricsScript] = useState<string | null>(null);
  // #265 — Currently selected languages for the Translation column.
  // CHANGED from single `translationLang: string` to multi `translationLangs:
  // string[]`. User: "in the selection of lyrics it should allow only one
  // language. in translation, it is ok to have more than one." So lyrics
  // stays single-select (lyricsScript above), translation becomes a
  // chip-based multi-select. Default to ["english"] — same first-load
  // behaviour as before.
  const [translationLangs, setTranslationLangs] = useState<string[]>(["english"]);
  const [sheetReady, setSheetReady] = useState<boolean | null>(null);
  // #271 — User: "on download and save, we should option to choose
  // mp3 and document. select all, select only song/doc". Two
  // checkboxes above the action buttons; defaults are both ON (the
  // previous behaviour). Download + Save-to-Drive honor these flags;
  // Print is unaffected (always doc-only by design).
  const [includeMp3, setIncludeMp3] = useState(true);
  const [includeDoc, setIncludeDoc] = useState(true);
  // Per-artifact Drive upload status. Auto-fires once we've successfully
  // fetched the artifact from Fly AND we have a parent folder ID.
  const [driveUploads, setDriveUploads] = useState<Record<ArtifactName, DriveUpload>>({
    audio_mp3:      { status: "idle" },
    lyrics:         { status: "idle" },
    translation:    { status: "idle" },
    practice_sheet: { status: "idle" },
  });

  // #250 — User feedback: song rating + per-verse translation edits
  // + per-verse quality flags. Stored as a single JSON sidecar on
  // Fly's volume (see backend/ojaswati_backend/routers/artifact_feedback.py).
  // Fetched once on mount; patched optimistically on every edit.
  const [feedback, setFeedback] = useState<SongFeedback>(EMPTY_FEEDBACK);
  // #270b — Track whether the initial feedback fetch has completed.
  // The canonical effect waits for this before deciding to hit Gemini —
  // otherwise it races with the fetch and triggers a needless Gemini
  // call when feedback.canonical_lyrics IS cached but hasn't loaded
  // yet (user reported: "I don't want this to go to Gemini every time
  // I press play button"). Set to true on BOTH success and failure so
  // a dead sidecar fetch doesn't deadlock the canonical path forever.
  const [feedbackLoaded, setFeedbackLoaded] = useState(false);
  useEffect(() => {
    let abort = false;
    fetchFeedback(jobId)
      .then((data) => {
        if (!abort) {
          setFeedback(data);
          setFeedbackLoaded(true);
        }
      })
      .catch((err) => {
        // Feedback is non-critical — never block the song page just
        // because the sidecar fetch failed. Log silently.
        console.warn("[song-feedback] fetch failed:", err);
        if (!abort) setFeedbackLoaded(true);
      });
    return () => {
      abort = true;
    };
  }, [jobId]);

  /** Optimistic write: update local state immediately, fire the PATCH
   *  in the background. If it fails we revert + toast. */
  const updateFeedback = (partial: SongFeedback) => {
    setFeedback((prev) => {
      // Local merge mirrors the server's _merge() so the UI looks
      // correct between optimistic update and the server confirmation.
      const next: SongFeedback = { ...prev };
      if (partial.rating !== undefined) next.rating = partial.rating;
      for (const key of ["edits", "verse_flags"] as const) {
        const inc = partial[key];
        if (!inc) continue;
        const cur = { ...(next[key] ?? {}) };
        for (const [lang, verseMap] of Object.entries(inc)) {
          const langCur = { ...(cur[lang] ?? {}) };
          for (const [idx, value] of Object.entries(verseMap)) {
            if (value === "") delete langCur[idx];
            else (langCur as Record<string, any>)[idx] = value;
          }
          if (Object.keys(langCur).length) cur[lang] = langCur as any;
          else delete cur[lang];
        }
        (next as any)[key] = cur;
      }
      // #264 — meta_overrides is a flat dict (singer/composer/raga/…).
      // Mirror the server's _merge: empty string deletes the key.
      if (partial.meta_overrides) {
        const curMeta = { ...(next.meta_overrides ?? {}) };
        for (const [k, v] of Object.entries(partial.meta_overrides)) {
          if (v === "") delete curMeta[k];
          else curMeta[k] = v;
        }
        next.meta_overrides = curMeta;
      }
      // #267 — canonical_lyrics is a REPLACE field (not merged).
      // Sending a non-empty array overwrites the cache; sending an
      // empty array clears it.
      if (partial.canonical_lyrics !== undefined) {
        next.canonical_lyrics =
          partial.canonical_lyrics.length > 0
            ? partial.canonical_lyrics
            : undefined;
      }
      return next;
    });
    patchFeedback(jobId, partial).catch((err) => {
      console.error("[song-feedback] patch failed:", err);
      toast.error("Couldn't save your edit — check connection");
      // Best-effort revert: re-fetch the authoritative state.
      fetchFeedback(jobId).then(setFeedback).catch(() => {});
    });
  };

  // #263 — Canonical lyrics auto-fetch. Fires on mount when youtubeUrl
  // is provided. The Gemini-grounded path (#262f) returns authentic
  // Pallavi/Anupallavi/Charanam sections for documented compositions.
  // When successful, REPLACES the whisper-based verse table with a
  // clean canonical view. When Gemini doesn't recognize the song or
  // returns no lyrics, canonical stays null and the existing whisper
  // view is shown instead. No karaoke timing on canonical (per user
  // decision — simplicity over polish).
  //
  // ORDER NOTE: this block sits AFTER feedback state + updateFeedback
  // because fetchCanonicalNow calls updateFeedback() to cache the
  // result, and the cache-check useEffect reads feedback.canonical_lyrics.
  // Both would hit a temporal-dead-zone if declared earlier. (#267)
  const [canonical, setCanonical] = useState<
    Array<{
      section: string;
      sanskrit: string;
      transliteration: string;
      translation: string;
    }> | null
  >(null);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [canonicalError, setCanonicalError] = useState<string | null>(null);
  const canonicalAttempted = useRef(false);

  // #267a — Build a "whisper hint" from the first ~30s of transcribed
  // verses. Sent to the backend's /songs/identify endpoint to help
  // Gemini detect a VRITTAM (free-form preamble verse common in
  // Karnatic/Tamil-devotional renditions). Logic:
  //  - Gemini knows the canonical Pallavi of the named composition.
  //  - If the audio's actual opening doesn't match that Pallavi, the
  //    difference is almost always a vrittam.
  //  - Whisper text is messy (especially on Sanskrit) but the
  //    PATTERN — "does this match the known Pallavi or not" — is
  //    reliable. We cap to ~6 lines / 30s / 600 chars to keep the
  //    prompt small.
  const buildWhisperHint = (): string => {
    if (!versesTimed || versesTimed.length === 0) return "";
    const lines: string[] = [];
    for (const v of versesTimed) {
      if (lines.length >= 6) break;
      // Stop at 35s — vrittams typically run 15-30s; anything past
      // that is the main composition starting up.
      if (typeof v.start === "number" && v.start > 35) break;
      const t = (v.text ?? "").trim();
      if (!t) continue;
      lines.push(t);
    }
    return lines.join("\n");
  };

  // #267 — fetchCanonical extracted so the "Refresh canonical" button
  // can call it too (forces re-fetch, ignoring the cached version).
  const fetchCanonicalNow = async (skipCache: boolean) => {
    if (!youtubeUrl) return;
    setCanonicalLoading(true);
    setCanonicalError(null);
    try {
      const whisperHint = buildWhisperHint();
      const res = await fetch(`${apiUrl}/songs/identify`, {
        method: "POST",
        headers: apiHeaders({
          "content-type": "application/json",
          accept: "application/json",
        }),
        body: JSON.stringify({
          url: youtubeUrl,
          title: songTitle || songMeta?.title || "",
          include_lyrics: true,
          // #267a — Empty string omitted server-side; only sent when
          // we actually have whisper verses to share.
          whisper_hint: whisperHint || undefined,
        }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as {
        recognized?: boolean;
        canonical_lyrics?: Array<{
          section: string;
          sanskrit: string;
          transliteration: string;
          translation: string;
        }>;
        title?: string;
        composer?: string;
      };
      if (data.recognized && (data.canonical_lyrics?.length ?? 0) > 0) {
        const lyrics = data.canonical_lyrics ?? [];
        setCanonical(lyrics);
        // #267 — Cache to feedback sidecar so next page load skips
        // the Gemini call. updateFeedback patches optimistically;
        // failure is non-fatal (we already showed the lyrics).
        updateFeedback({ canonical_lyrics: lyrics });
        if (skipCache) {
          toast.success("Refreshed canonical lyrics from Gemini", {
            duration: 2500,
          });
        }
      } else {
        setCanonical(null);
        setCanonicalError(null);
      }
    } catch (e) {
      console.warn("[canonical] fetch failed:", e);
      setCanonical(null);
      setCanonicalError(null);
    } finally {
      setCanonicalLoading(false);
    }
  };

  useEffect(() => {
    if (!youtubeUrl || canonicalAttempted.current) return;
    // #270b — WAIT for feedback fetch to complete before deciding.
    // Without this gate, the effect could race the sidecar fetch:
    // versesTimed arrives → effect re-runs with delay=0 → Gemini call
    // fires → THEN feedback (with cached canonical_lyrics!) arrives
    // too late. That's exactly the bug the user hit ("I don't want
    // this to go to Gemini every time").
    if (!feedbackLoaded) return;
    // #267 — Read from cache first. If the feedback sidecar has
    // canonical_lyrics from a previous fetch, use it directly and
    // SKIP the Gemini call entirely. Saves ~$0.035 per page load and
    // ~30-50s of grounding latency.
    const cached = feedback.canonical_lyrics;
    if (cached && cached.length > 0) {
      canonicalAttempted.current = true;
      setCanonical(cached);
      // Don't fetch — user can hit Refresh if they want a re-fetch.
      return;
    }
    // #267a — No cache yet. Defer the Gemini call briefly so the
    // whisper transcript (versesTimed) has a chance to load — its
    // opening lines feed the vrittam-detection hint. If verses are
    // already loaded, fire immediately; otherwise wait up to 2.5s.
    // The effect re-runs when versesTimed.length changes, so the
    // moment whisper arrives we re-schedule with delay 0.
    const haveVerses = versesTimed && versesTimed.length > 0;
    const delay = haveVerses ? 0 : 2500;
    const timer = setTimeout(() => {
      canonicalAttempted.current = true;
      void fetchCanonicalNow(false);
    }, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [youtubeUrl, feedbackLoaded, feedback.canonical_lyrics, versesTimed.length]);

  // Wraps uploadArtifactToDrive with the per-artifact state update.
  // Called from the fetch effect after each artifact lands.
  const pushToDrive = async (name: ArtifactName, blob: Blob) => {
    if (!driveFolderId) {
      setDriveUploads((p) => ({ ...p, [name]: { status: "skipped" } }));
      return;
    }
    setDriveUploads((p) => ({ ...p, [name]: { status: "uploading" } }));
    try {
      const webViewLink = await uploadArtifactToDrive({
        blob,
        filename: driveFilenameFor(name, jobId, songTitle ?? songMeta?.title ?? ""),
        mimeType: DRIVE_MIME[name],
        parentFolderId: driveFolderId,
        subfolderName: songMeta ? driveSubfolderFor(songMeta) : undefined,
        properties: songMeta ? driveCustomProperties(songMeta, name) : undefined,
      });
      setDriveUploads((p) => ({
        ...p,
        [name]: { status: "done", webViewLink: webViewLink ?? undefined },
      }));
    } catch (e: any) {
      setDriveUploads((p) => ({
        ...p,
        [name]: { status: "failed", error: e?.message ?? "Upload failed" },
      }));
    }
  };

  // Cancel + revoke blob URLs on unmount or job change so we don't
  // leak memory across re-runs.
  useEffect(() => {
    let cancelled = false;
    const audioObjectUrls: string[] = [];

    (async () => {
      // Audio — blob URL pattern so the <audio> element gets a URL
      // that doesn't need the auth header. We also keep the blob
      // itself so the Drive upload can reuse the bytes without
      // re-fetching from Fly.
      setAudio({ status: "loading" });
      try {
        const r = await fetchArtifact(apiUrl, jobId, "audio_mp3");
        if (cancelled) return;
        if (r.status === 404) {
          setAudio({ status: "missing" });
          setDriveUploads((p) => ({ ...p, audio_mp3: { status: "skipped" } }));
        } else if (!r.ok) {
          setAudio({ status: "error", error: `${r.status} ${r.statusText}` });
          setDriveUploads((p) => ({ ...p, audio_mp3: { status: "skipped" } }));
        } else {
          const blob = await r.blob();
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          audioObjectUrls.push(url);
          setAudio({ status: "ready", data: url });
          // Fire-and-forget Drive upload; UI tracks via driveUploads state.
          void pushToDrive("audio_mp3", blob);
        }
      } catch (e: any) {
        if (!cancelled) {
          setAudio({ status: "error", error: e?.message ?? "Fetch failed" });
          setDriveUploads((p) => ({ ...p, audio_mp3: { status: "skipped" } }));
        }
      }

      // Lyrics + translation — fetched in parallel as text.
      const [lr, tr] = await Promise.all([
        fetchArtifact(apiUrl, jobId, "lyrics").catch((e) => ({ ok: false, status: 0, statusText: e?.message ?? "fetch" })) as any,
        fetchArtifact(apiUrl, jobId, "translation").catch((e) => ({ ok: false, status: 0, statusText: e?.message ?? "fetch" })) as any,
      ]);
      if (cancelled) return;
      if (lr.status === 404) {
        setLyrics({ status: "missing" });
        setDriveUploads((p) => ({ ...p, lyrics: { status: "skipped" } }));
      } else if (!lr.ok) {
        setLyrics({ status: "error", error: `${lr.status} ${lr.statusText}` });
        setDriveUploads((p) => ({ ...p, lyrics: { status: "skipped" } }));
      } else {
        const t = await lr.text();
        if (!cancelled) {
          setLyrics({ status: "ready", data: t });
          // #233 — Don't upload the bare lyrics.txt anymore; everything
          // lands in the combined "<title> — lyrics + translation.html"
          // emitted after both columns load (below).
          setDriveUploads((p) => ({ ...p, lyrics: { status: "skipped" } }));
        }
      }
      if (tr.status === 404) {
        setTranslation({ status: "missing" });
        setDriveUploads((p) => ({ ...p, translation: { status: "skipped" } }));
      } else if (!tr.ok) {
        setTranslation({ status: "error", error: `${tr.status} ${tr.statusText}` });
        setDriveUploads((p) => ({ ...p, translation: { status: "skipped" } }));
      } else {
        const t = await tr.text();
        if (!cancelled) {
          setTranslation({ status: "ready", data: t });
          // #233 — Skip standalone translation.txt upload too.
          setDriveUploads((p) => ({ ...p, translation: { status: "skipped" } }));
        }
      }

      // transliterations.json + translations.json — power the two
      // dropdown pickers in the side-by-side viewer (#223). Both are
      // best-effort: pre-#223 jobs won't have these files, and the
      // viewer falls back to lyrics.txt + translation.txt. Cast through
      // ArtifactName because the union models Drive-uploadable artifacts
      // only — these sidecars are fetch-only.
      try {
        const r = await fetchArtifact(apiUrl, jobId, "transliterations" as ArtifactName);
        if (!cancelled && r.ok) {
          const parsed = (await r.json()) as Record<string, string[]>;
          setTransliterations(parsed || {});
        }
      } catch { /* not generated → fall back to lyrics.txt */ }
      try {
        const r = await fetchArtifact(apiUrl, jobId, "translations" as ArtifactName);
        if (!cancelled && r.ok) {
          const parsed = (await r.json()) as Record<string, string[]>;
          setAllTranslations(parsed || {});
        }
      } catch { /* not generated → fall back to translation.txt */ }
      // verses.json — structured verse list with per-verse audio
      // start/end timings (#224). Powers karaoke highlighting + click-
      // to-seek in the viewer. Pre-#224 jobs don't have this; the
      // viewer silently falls back to text-only display.
      try {
        const r = await fetchArtifact(apiUrl, jobId, "verses" as ArtifactName);
        if (!cancelled && r.ok) {
          const parsed = (await r.json()) as Array<{ sequence: number; text: string; start: number; end: number; source_script: string }>;
          setVersesTimed(parsed || []);
        }
      } catch { /* no karaoke for old jobs */ }

      // #233 — Skip the server-rendered practice_sheet.md upload; we
      // generate a much nicer self-contained HTML doc here on the
      // client (lyrics + translation side-by-side, print-ready,
      // Drive-previewable) once both columns have loaded. Probe
      // existence locally only so the "Download practice sheet" link
      // still works for users who want the markdown version.
      try {
        const r = await fetchArtifact(apiUrl, jobId, "practice_sheet");
        if (!cancelled) setSheetReady(r.ok);
      } catch {
        if (!cancelled) setSheetReady(false);
      }
    })();

    return () => {
      cancelled = true;
      for (const u of audioObjectUrls) URL.revokeObjectURL(u);
    };
  }, [apiUrl, jobId]);

  // #233 — Combined lyrics-and-translation HTML upload.
  //
  // Each song in the Learning Library now lands in Drive as exactly
  // TWO files (per user feedback: "i want to have mp3 and lyrics +
  // translate document for each song"):
  //
  //   <title>.mp3                              — the audio
  //   <title> — lyrics + translation.html      — side-by-side, printable
  //
  // The HTML is generated here on the client from the loaded lyrics
  // and translation text, then uploaded once via pushToDrive (which
  // routes through the Cloudflare Function with the user's Drive
  // session). One-shot guarded by combinedDocSentRef so a stale
  // re-render or a dropdown switch doesn't re-upload.
  const combinedDocSentRef = useRef(false);
  useEffect(() => {
    // #263 — Practice sheet (combined lyrics+translation HTML) upload
    // disabled per user feedback: "I don't need practice sheet. I need
    // the song and canonical lyrics with translation."
    //
    // The Drive flow now uploads only the MP3. Lyrics + translation are
    // shown in-app (canonical-first if Gemini recognises the song,
    // whisper-table fallback otherwise). Future Phase 2: upload
    // canonical_lyrics.json alongside the MP3 so the data is backed up
    // outside the app too.
    //
    // We still mark the practice_sheet slot as "skipped" so the
    // DriveBadge for that artifact never renders.
    if (combinedDocSentRef.current) return;
    combinedDocSentRef.current = true;
    setDriveUploads((p) => ({ ...p, practice_sheet: { status: "skipped" } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Practice sheet download — fetch with auth, then trigger a save.
  const downloadSheet = async () => {
    try {
      const r = await fetchArtifact(apiUrl, jobId, "practice_sheet");
      if (!r.ok) {
        toast.error("Practice sheet missing", { description: `${r.status} ${r.statusText}` });
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `practice_sheet-${jobId.slice(0, 8)}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      toast.error("Download failed", { description: e?.message ?? "" });
    }
  };

  // Tiny per-artifact Drive-upload status indicator. Slotted into the
  // top-right of each artifact's header so playback + upload status sit
  // side by side instead of two separate panels.
  const DriveBadge = ({ name }: { name: ArtifactName }) => {
    const up = driveUploads[name];
    if (!driveFolderId) return null;
    if (up.status === "idle" || up.status === "skipped") return null;
    if (up.status === "uploading") {
      return (
        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
          <Loader2 className="h-2.5 w-2.5 animate-spin" /> uploading to Drive…
        </span>
      );
    }
    if (up.status === "done") {
      return up.webViewLink ? (
        <a
          href={up.webViewLink}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] text-primary hover:underline inline-flex items-center gap-1"
        >
          ↗ open in Drive
        </a>
      ) : (
        <span className="text-[10px] text-primary inline-flex items-center gap-1">
          <Check className="h-2.5 w-2.5" /> in Drive
        </span>
      );
    }
    if (up.status === "failed") {
      // Expose the actual error inline (truncated to keep the badge
      // compact) AND in the tooltip. The old "Drive upload failed"
      // single-line gave no clue whether it was an expired Drive
      // session, a CORS issue, or a 5xx — diagnosing required opening
      // DevTools. Now the user can see the reason at a glance.
      const reason = (up.error || "").slice(0, 80);
      const isAuth = /revoked|sign.?in|401|unauthor/i.test(up.error || "");
      return (
        <span
          className="text-[10px] text-destructive inline-flex items-center gap-1 max-w-[420px]"
          title={up.error}
        >
          <AlertCircle className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">
            Drive: {reason || "upload failed"}
          </span>
          {isAuth && (
            <a
              href="/iHub"
              className="underline shrink-0"
              title="Open iHub to reconnect Drive"
            >
              reconnect
            </a>
          )}
        </span>
      );
    }
    return null;
  };

  return (
    <SectionCard title={title ?? "Generated artifacts"} subtitle={`Job ${jobId.slice(0, 8)}…`}>
      <div className="space-y-4">
        {!driveFolderId && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11px] text-amber-700 dark:text-amber-300">
            No Drive folder configured. Set one in Settings → Song Workspace
            defaults to auto-upload artifacts.
          </div>
        )}
        {/* Audio — #250: sticky to top of scroll container on mobile +
            desktop so the user can pause/seek without scrolling back up
            from the verses below. `top-0` works because the parent
            SectionCard scrolls inside the page, not a fixed window
            offset. backdrop-blur keeps content underneath legible. */}
        <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 border-b border-border/40">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
              <FileAudio className="h-3 w-3" /> Audio MP3
              <DriveBadge name="audio_mp3" />
            </div>
            {audio.status === "missing" && (
              <span className="text-[10px] text-muted-foreground/70">— not generated —</span>
            )}
            {audio.status === "error" && (
              <span className="text-[10px] text-destructive">{audio.error}</span>
            )}
          </div>
          {audio.status === "loading" && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          )}
          {audio.status === "ready" && audio.data && (
            <audio
              ref={artifactAudioRef}
              src={audio.data}
              controls
              className="w-full"
              preload="metadata"
            />
          )}
        </div>

        {/* #250 — Song-level star rating. Lives right under the player
            so it's the next thing the user sees when she comes back to
            a song she's been practicing. Same StarRating component as
            the metadata form, just bound to feedback.rating. */}
        <div className="flex items-center justify-between flex-wrap gap-2 -mt-1">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Your rating
          </span>
          <StarRating
            value={feedback.rating ?? 0}
            onChange={(n) => updateFeedback({ rating: n })}
          />
        </div>

        {/* #264 — Song details editor. The queue row's metadata
            (composer/raga/language) is captured at upload time and
            sometimes incomplete or wrong; "singer" isn't captured at
            all. This card lets the user fix any of them — overrides
            live in feedback.meta_overrides and override the queue-row
            values everywhere the song is displayed (workspace, PDF
            export, Drive filenames). Auto-saves on blur, no explicit
            "save" needed for these fields. */}
        <SongDetailsEditor
          songMeta={songMeta}
          metaOverrides={feedback.meta_overrides ?? {}}
          onChange={(key, value) =>
            updateFeedback({ meta_overrides: { [key]: value } })
          }
        />

        {/* #263 — Canonical-first lyrics view. When Gemini-grounded
            returned authentic sections (Pallavi / Anupallavi / Charanams),
            show those as the primary view — no karaoke, just clean text.
            Otherwise fall back to the whisper-based verse table that
            has karaoke timing. */}
        {canonicalLoading ? (
          <div className="rounded-lg border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground inline-flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Fetching canonical lyrics…
          </div>
        ) : canonical && canonical.length > 0 ? (
          <CanonicalLyricsView
            sections={canonical}
            driveBadges={{
              lyrics: <DriveBadge name="lyrics" />,
              translation: <DriveBadge name="translation" />,
            }}
            // #274 — User can edit any verse; Save persists to the
            // feedback sidecar so the edits flow into the PDF + Drive
            // upload + survive page reloads.
            onSave={(editedSections) => {
              setCanonical(editedSections);
              updateFeedback({ canonical_lyrics: editedSections });
              toast.success("Lyrics & translation saved", { duration: 2000 });
            }}
          />
        ) : (
          /* Fallback — whisper-based view for songs Gemini doesn't
             recognize, or when the canonical fetch failed. Same
             interactive table the user had before #263. */
          <LyricsTranslationViewer
            lyrics={lyrics}
            translation={translation}
            transliterations={transliterations}
            allTranslations={allTranslations}
            lyricsScript={lyricsScript}
            setLyricsScript={setLyricsScript}
            translationLangs={translationLangs}
            setTranslationLangs={setTranslationLangs}
            versesTimed={versesTimed}
            audioRef={artifactAudioRef}
            songTitle={songTitle}
            songMeta={songMeta}
            driveBadges={{
              lyrics: <DriveBadge name="lyrics" />,
              translation: <DriveBadge name="translation" />,
            }}
            feedback={feedback}
            onEditTranslation={(lang, verseIdx, text) =>
              updateFeedback({ edits: { [lang]: { [String(verseIdx)]: text } } })
            }
            onEditLyrics={(script, verseIdx, text) =>
              updateFeedback({ lyrics_edits: { [script]: { [String(verseIdx)]: text } } })
            }
            onSetVerseFlag={(lang, verseIdx, flag) =>
              updateFeedback({
                verse_flags: { [lang]: { [String(verseIdx)]: flag ?? "" } },
              })
            }
          />
        )}
        {canonicalError && (
          <p className="text-[11px] text-amber-700 dark:text-amber-400 -mt-2">
            {canonicalError}
          </p>
        )}

        {/* #264 — Save & Download as PDF. The user wants a single
            artifact she can keep that bundles the song info + lyrics
            + translation. Uses the browser's built-in print-to-PDF
            (window.print on a styled new tab) so we don't carry a
            heavy PDF library. The "save" half of the button name
            covers the metadata persistence: any pending edits in the
            SongDetailsEditor above already auto-save on blur, so by
            the time you click here everything is on the server. */}
        {/* #266 — Three separate actions: Download (MP3+doc),
            Save to Drive (MP3+doc), Print (doc only). Per user:
            "there has to be download (mp3+ doc), save to drive
            (mp3+ doc), print (doc)". */}
        {/* #271 — Include MP3/Document toggles. Download + Save-to-Drive
            both honor these; Print is unaffected. "Select all" is the
            shorthand for "check both" — exposed as a tiny inline link
            so the row stays compact. */}
        <div className="flex items-center justify-end gap-3 pt-2 text-[11px] text-muted-foreground flex-wrap">
          <span className="uppercase tracking-wider">Include:</span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <Checkbox
              checked={includeMp3}
              onCheckedChange={(v) => setIncludeMp3(v === true)}
              aria-label="Include MP3"
            />
            <span>MP3</span>
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <Checkbox
              checked={includeDoc}
              onCheckedChange={(v) => setIncludeDoc(v === true)}
              aria-label="Include document"
            />
            <span>Document</span>
          </label>
          <button
            type="button"
            className="text-[11px] underline-offset-2 hover:underline disabled:opacity-50"
            disabled={includeMp3 && includeDoc}
            onClick={() => {
              setIncludeMp3(true);
              setIncludeDoc(true);
            }}
            title="Check both"
          >
            Select all
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1 flex-wrap">
          {/* DOWNLOAD — both MP3 + lyrics doc to local device */}
          <Button
            size="sm"
            variant="outline"
            disabled={audio.status !== "ready"}
            onClick={async () => {
              const active = document.activeElement as HTMLElement | null;
              if (active && typeof active.blur === "function") active.blur();
              await new Promise((r) => requestAnimationFrame(() => r(null)));
              // #271 — Honour the include-MP3 / include-Document
              // checkboxes. If nothing is selected, bail with a warning
              // instead of doing a no-op silently.
              if (!includeMp3 && !includeDoc) {
                toast.warning("Pick MP3 or Document to download", {
                  description: "Use the checkboxes above the buttons.",
                });
                return;
              }
              const eff = mergeMeta(songMeta, feedback.meta_overrides);
              const titleForFile = songTitle ?? eff.title ?? "Song";
              const safeName = titleForFile.replace(/[^\p{L}\p{N} _.-]+/gu, "_");
              const parts: string[] = [];
              // MP3 — re-fetch as a Blob so we can name the download
              // (the audio.data object URL is great for playback but
              // setting `download` on an object-URL <a> doesn't always
              // give a meaningful filename on every browser).
              if (includeMp3) {
                try {
                  const r = await fetchArtifact(apiUrl, jobId, "audio_mp3");
                  if (r.ok) {
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${safeName}.mp3`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    parts.push("MP3");
                  }
                } catch (e) {
                  console.error("[download] MP3 failed:", e);
                  toast.warning("MP3 download failed", {
                    description: e instanceof Error ? e.message : "",
                  });
                }
              }
              // #270 — Lyrics + translation as PDF (was HTML). Built
              // locally via html2pdf; lib loads on first use.
              if (includeDoc) {
                try {
                  const pdfBlob = await buildSongPdfBlob({
                    title: titleForFile,
                    composer: eff.composer,
                    singer: (feedback.meta_overrides?.singer ?? "").trim(),
                    raga: eff.raga,
                    language: eff.language,
                    rating: feedback.rating ?? 0,
                    canonical,
                    lyricsText: (lyrics.status === "ready" ? lyrics.data : "") || "",
                    translationText: (translation.status === "ready" ? translation.data : "") || "",
                  });
                  const docUrl = URL.createObjectURL(pdfBlob);
                  const docA = document.createElement("a");
                  docA.href = docUrl;
                  docA.download = `${safeName} — Lyrics & Translation.pdf`;
                  document.body.appendChild(docA);
                  docA.click();
                  docA.remove();
                  setTimeout(() => URL.revokeObjectURL(docUrl), 60_000);
                  parts.push("PDF");
                } catch (e) {
                  console.error("[download] PDF build failed:", e);
                  toast.warning("Couldn't build the lyrics PDF", {
                    description: e instanceof Error ? e.message : "",
                  });
                }
              }
              if (parts.length > 0) {
                toast.success(`Downloaded ${parts.join(" + ")}`, { duration: 2500 });
              }
            }}
            className="gap-1.5"
            title="Download MP3 and lyrics doc to this device"
          >
            <Download className="h-3.5 w-3.5" />
            Download
          </Button>

          {/* SAVE TO DRIVE — MP3 + lyrics doc into the song subfolder */}
          <Button
            size="sm"
            variant="outline"
            disabled={!driveFolderId}
            onClick={async () => {
              const active = document.activeElement as HTMLElement | null;
              if (active && typeof active.blur === "function") active.blur();
              await new Promise((r) => requestAnimationFrame(() => r(null)));
              if (!driveFolderId) {
                toast.warning("No Drive folder set", {
                  description: "Pick a Drive folder when adding songs to enable this.",
                });
                return;
              }
              // #271 — Honor the include-MP3/include-Document toggles.
              if (!includeMp3 && !includeDoc) {
                toast.warning("Pick MP3 or Document to save", {
                  description: "Use the checkboxes above the buttons.",
                });
                return;
              }
              const eff = mergeMeta(songMeta, feedback.meta_overrides);
              const titleForFile = songTitle ?? eff.title ?? "Song";
              const safeName = titleForFile.replace(/[^\p{L}\p{N} _.-]+/gu, "_");
              const parts: string[] = [];
              let docFailed = false;
              // MP3 — only re-upload if it isn't already on Drive
              // (auto-upload runs once on page load via pushToDrive).
              if (includeMp3) {
                const mp3Status = driveUploads.audio_mp3?.status;
                if (mp3Status !== "done" && mp3Status !== "uploading") {
                  try {
                    const r = await fetchArtifact(apiUrl, jobId, "audio_mp3");
                    if (r.ok) {
                      const blob = await r.blob();
                      await pushToDrive("audio_mp3", blob);
                    }
                  } catch (e) {
                    console.error("[save-drive] MP3 upload failed:", e);
                  }
                }
                parts.push("MP3");
              }
              // #270 — Lyrics + translation as PDF (was HTML). The Drive
              // file lands as a real .pdf so Drive previews it nicely
              // and the user doesn't need to open it in a browser tab.
              if (includeDoc) {
                try {
                  setDriveUploads((p) => ({
                    ...p,
                    practice_sheet: { status: "uploading" },
                  }));
                  const pdfBlob = await buildSongPdfBlob({
                    title: titleForFile,
                    composer: eff.composer,
                    singer: (feedback.meta_overrides?.singer ?? "").trim(),
                    raga: eff.raga,
                    language: eff.language,
                    rating: feedback.rating ?? 0,
                    canonical,
                    lyricsText: (lyrics.status === "ready" ? lyrics.data : "") || "",
                    translationText: (translation.status === "ready" ? translation.data : "") || "",
                  });
                  const webViewLink = await uploadArtifactToDrive({
                    blob: pdfBlob,
                    filename: `${safeName} — Lyrics & Translation.pdf`,
                    mimeType: "application/pdf",
                    parentFolderId: driveFolderId,
                    subfolderName: songMeta ? driveSubfolderFor(songMeta) : undefined,
                    properties: songMeta
                      ? driveCustomProperties(songMeta, "practice_sheet")
                      : undefined,
                  });
                  setDriveUploads((p) => ({
                    ...p,
                    practice_sheet: { status: "done", webViewLink: webViewLink ?? undefined },
                  }));
                  parts.push("PDF");
                } catch (e) {
                  docFailed = true;
                  setDriveUploads((p) => ({
                    ...p,
                    practice_sheet: { status: "skipped" },
                  }));
                  toast.warning("Drive backup failed", {
                    description: e instanceof Error ? e.message : "",
                  });
                }
              }
              // Doc failure already showed its own warning toast. If
              // anything else succeeded, surface a success so the user
              // sees what landed.
              if (parts.length > 0) {
                toast.success(`Saved ${parts.join(" + ")} to Drive`, {
                  description: "Backed up to your Learning Library folder.",
                  duration: 3000,
                });
              }
              void docFailed;
            }}
            className="gap-1.5"
            title="Save MP3 and lyrics doc to your Drive Learning Library folder"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Save to Drive
          </Button>

          {/* PRINT — opens print dialog for the lyrics doc only */}
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              const active = document.activeElement as HTMLElement | null;
              if (active && typeof active.blur === "function") active.blur();
              await new Promise((r) => requestAnimationFrame(() => r(null)));
              const eff = mergeMeta(songMeta, feedback.meta_overrides);
              openPrintableSongPage({
                title: songTitle ?? eff.title ?? "Song",
                composer: eff.composer,
                singer: (feedback.meta_overrides?.singer ?? "").trim(),
                raga: eff.raga,
                language: eff.language,
                rating: feedback.rating ?? 0,
                canonical,
                lyricsText: (lyrics.status === "ready" ? lyrics.data : "") || "",
                translationText: (translation.status === "ready" ? translation.data : "") || "",
              });
            }}
            className="gap-1.5 border-violet-300 dark:border-violet-500/30"
            title="Open the lyrics doc in a new tab and print (save as PDF)"
          >
            <FileMusic className="h-3.5 w-3.5" />
            Print
          </Button>
        </div>
        {/* #263 — Practice sheet section removed per user request. */}
      </div>
    </SectionCard>
  );
}

/* =====================================================================
   SongDetailsEditor — editable metadata card (#264).

   Title is read-only (set at upload time from YouTube). Composer / raga
   / singer are editable. Each input auto-saves on blur via onChange
   callback (which patches feedback.meta_overrides).

   Singer is the new field — Gemini gives us composer (writer of the
   song), but the performer (e.g. Sooryagayathri) isn't auto-extracted.
   User types it here.

   "Reset" link clears the override and falls back to the queue-row
   value (sends empty string, which the backend treats as delete).
   ===================================================================== */
function SongDetailsEditor({
  songMeta,
  metaOverrides,
  onChange,
}: {
  songMeta?: SongMeta;
  metaOverrides: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  // Effective value = override if present, else fall back to original.
  const eff = (key: keyof SongMeta) =>
    metaOverrides[key as string] ?? songMeta?.[key] ?? "";
  return (
    <section className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
        <Settings2 className="h-3 w-3" /> Song details
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <EditableMetaField
          label="Singer"
          placeholder="e.g. Sooryagayathri"
          value={metaOverrides.singer ?? ""}
          onSave={(v) => onChange("singer", v)}
        />
        <EditableMetaField
          label="Composer"
          placeholder="e.g. Swami Dayanand Saraswati"
          value={metaOverrides.composer ?? songMeta?.composer ?? ""}
          onSave={(v) => {
            // Only persist as override if it differs from the original
            // queue-row value — otherwise clear the override so we
            // don't pin the same string forever.
            if (v.trim() === (songMeta?.composer ?? "").trim()) {
              if (metaOverrides.composer !== undefined) onChange("composer", "");
            } else {
              onChange("composer", v);
            }
          }}
        />
        <EditableMetaField
          label="Raga"
          placeholder="e.g. Kalyani"
          value={metaOverrides.raga ?? songMeta?.raga ?? ""}
          onSave={(v) => {
            if (v.trim() === (songMeta?.raga ?? "").trim()) {
              if (metaOverrides.raga !== undefined) onChange("raga", "");
            } else {
              onChange("raga", v);
            }
          }}
        />
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Language
          </label>
          <div className="text-sm px-2 py-1.5">{eff("language") || "—"}</div>
        </div>
      </div>
      {/* #271 — Explicit Save affordance. Each EditableMetaField already
          saves on blur, so this button's job is twofold:
          (1) make the persistence visible — user can click and KNOW
              their edits landed (previously confusing — blur-save is
              invisible);
          (2) flush any pending draft text that the user didn't blur
              yet — clicking the button blurs the active field, which
              triggers its onBlur → onSave handler.
          The success toast confirms what happened. */}
      <div className="flex items-center justify-end pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            const active = document.activeElement as HTMLElement | null;
            if (active && typeof active.blur === "function") active.blur();
            toast.success("Song details saved", {
              description: "Editable fields are persisted to your library.",
              duration: 2000,
            });
          }}
          className="gap-1.5"
          title="Persist any pending edits to the singer / composer / raga fields"
        >
          <Save className="h-3.5 w-3.5" />
          Save details
        </Button>
      </div>
    </section>
  );
}

/** A single labelled text input that fires onSave on blur (not on every
 *  keystroke). Keeps a local draft so typing doesn't lag behind the
 *  server roundtrip. */
function EditableMetaField({
  label,
  placeholder,
  value,
  onSave,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  // Sync if the upstream value changes (e.g. after a re-fetch).
  useEffect(() => {
    setDraft(value);
  }, [value]);
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft.trim() !== value.trim()) onSave(draft.trim());
        }}
        className="h-8 text-sm"
      />
    </div>
  );
}

/** Resolve effective metadata = queue-row defaults overlaid with user
 *  overrides. Used by the PDF generator + anywhere else that needs the
 *  "what should we actually display" view. */
function mergeMeta(
  songMeta: SongMeta | undefined,
  overrides: Record<string, string> | undefined,
): SongMeta {
  const o = overrides ?? {};
  return {
    title: o.title || songMeta?.title || "",
    language: (o.language as SongLanguage) || songMeta?.language || "Sanskrit",
    composer: o.composer || songMeta?.composer || "",
    raga: o.raga || songMeta?.raga || "",
    album: o.album || songMeta?.album || "",
    year: o.year || songMeta?.year || "",
  };
}

/** Inputs accepted by both the printable-page launcher and the Drive
 *  HTML uploader. Kept as a type alias so the two stay in lockstep. */
type PrintableSongOpts = {
  title: string;
  composer: string;
  singer: string;
  raga: string;
  language: string;
  rating: number;
  canonical: Array<{
    section: string;
    sanskrit: string;
    transliteration: string;
    translation: string;
  }> | null;
  lyricsText: string;
  translationText: string;
};

/* #270 — Convert the song's print-styled HTML into a PDF Blob the
 * browser can hand to the Drive upload + the local download. Why:
 *   - The Save-to-Drive flow used to upload an .html file, which the
 *     user found inconvenient — Drive preview is uglier than a real
 *     PDF and printing requires an extra step.
 *   - User: "drive root should have pdf as file and not html".
 *
 * Implementation: render the full HTML doc inside a hidden iframe so
 * the embedded <style> block applies naturally (innerHTML on a div
 * doesn't include <head><style> reliably across browsers); then ask
 * html2pdf.js to rasterize the iframe's body to a multipage A4 PDF.
 * Lazy-import the lib so the ~400 KB html2canvas+jsPDF bundle only
 * loads when the user actually clicks Download or Save to Drive.
 *
 * Trade-off: html2pdf rasterizes content into JPEG pages, so PDF
 * text isn't selectable. That's acceptable here — Sanskrit + Tamil
 * scripts render correctly via the browser's normal font stack, and
 * the user's main use case is print + read, not text-search.
 */
async function buildSongPdfBlob(opts: PrintableSongOpts): Promise<Blob> {
  const html = buildSongHtml(opts);
  // Lazy import — keeps the initial bundle slim.
  const mod = (await import("html2pdf.js")) as any;
  const html2pdf = (mod.default || mod) as (...args: any[]) => any;

  // Render the full document in a hidden iframe so the embedded
  // <style> block applies. Position offscreen + size to A4 width so
  // typography lines up with the printed result.
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = "210mm";
  iframe.style.height = "297mm";
  iframe.style.border = "0";
  iframe.style.background = "#ffffff";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error("html2pdf: could not access iframe document");
    doc.open();
    doc.write(html);
    doc.close();

    // Give the browser one frame for layout + a tick for any web
    // fonts to settle. Devanagari + Tamil glyphs in particular need
    // the actual layout pass before html2canvas snapshots them.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => setTimeout(r, 100));

    const body = doc.body;
    if (!body) throw new Error("html2pdf: iframe body missing");
    // Grow the iframe to its content height so html2canvas captures
    // every page (otherwise it clips at 297mm).
    iframe.style.height = `${body.scrollHeight + 80}px`;

    const blob = (await html2pdf()
      .set({
        margin: [12, 14, 14, 14],
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          // Letting the lib measure the body itself is reliable when
          // the iframe is already sized to content.
          windowWidth: body.scrollWidth,
          windowHeight: body.scrollHeight,
          // #270a — html2canvas clones the captured element INTO the
          // parent document for rasterization. That cascades parent's
          // Tailwind v4 CSS (which uses oklch() for every theme token)
          // through to the clone, and html2canvas's color parser
          // throws on oklch ("Attempting to parse an unsupported color
          // function 'oklch'"). Inject hex fallbacks for every --*
          // token defined in src/styles.css so the cascade resolves
          // to plain hex before the parser sees it. The values track
          // the LIGHT-mode oklch from styles.css — approximated by eye
          // since exact oklch→sRGB conversion isn't needed (the song
          // HTML never actually uses these tokens; we just need the
          // parser not to throw when it walks the inherited cascade).
          onclone: (clonedDoc: Document) => {
            const style = clonedDoc.createElement("style");
            style.textContent = `
              :root, html, body {
                color-scheme: light !important;
                --background: #fdfaf5 !important;
                --foreground: #1a1a1a !important;
                --card: #ffffff !important;
                --card-foreground: #1a1a1a !important;
                --popover: #ffffff !important;
                --popover-foreground: #1a1a1a !important;
                --primary: #c87a3a !important;
                --primary-foreground: #fff8ee !important;
                --secondary: #f5ecdb !important;
                --secondary-foreground: #2c1f12 !important;
                --muted: #f0e6d2 !important;
                --muted-foreground: #7a6850 !important;
                --accent: #f4d8a8 !important;
                --accent-foreground: #2c1f12 !important;
                --destructive: #d84c2c !important;
                --destructive-foreground: #fff8ee !important;
                --success: #6b8f4e !important;
                --success-foreground: #fff8ee !important;
                --warning: #d4a04a !important;
                --warning-foreground: #2c1f12 !important;
                --border: #e5dcc8 !important;
                --input: #ecdfc6 !important;
                --ring: #c87a3a !important;
                --sidebar: #f8f1e4 !important;
                --sidebar-foreground: #2c1f12 !important;
                --sidebar-accent: #f0e0c5 !important;
                --sidebar-accent-foreground: #2c1f12 !important;
                --sidebar-border: #e5dcc8 !important;
              }
            `;
            (clonedDoc.head || clonedDoc.documentElement).insertBefore(
              style,
              (clonedDoc.head || clonedDoc.documentElement).firstChild,
            );
          },
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(body)
      .outputPdf("blob")) as Blob;
    return blob;
  } finally {
    document.body.removeChild(iframe);
  }
}

/** Build the self-contained, print-styled HTML for a song's lyrics +
 *  translation. Pure function — no DOM access. Used by both
 *  openPrintableSongPage (window.print path) and the Drive auto-upload
 *  (so the Drive copy is identical to what the user sees in print). */
function buildSongHtml(opts: PrintableSongOpts): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const nl2br = (s: string) => escape(s).replace(/\n/g, "<br/>");
  const stars = (n: number) =>
    "★".repeat(Math.max(0, Math.min(5, n))) +
    "☆".repeat(5 - Math.max(0, Math.min(5, n)));

  const metaRows = [
    ["Composer", opts.composer],
    ["Singer", opts.singer],
    ["Raga", opts.raga],
    ["Language", opts.language],
    ["Rating", opts.rating > 0 ? stars(opts.rating) : ""],
    ["Saved", new Date().toLocaleDateString()],
  ]
    .filter(([_, v]) => v && String(v).trim())
    .map(
      ([k, v]) =>
        `<tr><th>${escape(String(k))}</th><td>${escape(String(v))}</td></tr>`,
    )
    .join("");

  let lyricsHtml = "";
  if (opts.canonical && opts.canonical.length > 0) {
    lyricsHtml = opts.canonical
      .map(
        (v) => `
        <div class="section">
          <h3>${escape(v.section)}</h3>
          <p class="sanskrit">${nl2br(v.sanskrit)}</p>
          ${v.transliteration ? `<p class="iast">${nl2br(v.transliteration)}</p>` : ""}
          ${v.translation ? `<p class="en">${nl2br(v.translation)}</p>` : ""}
        </div>`,
      )
      .join("\n");
  } else {
    // Fallback: side-by-side from whisper data if canonical not loaded.
    const lyricsLines = opts.lyricsText
      .split(/\n\s*\n/)
      .map((b) => b.trim())
      .filter(Boolean);
    const translationLines = opts.translationText
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const rows = Math.max(lyricsLines.length, translationLines.length);
    if (rows === 0) {
      lyricsHtml = `<p class="empty">No lyrics available.</p>`;
    } else {
      lyricsHtml = `<table class="verses"><thead><tr><th>#</th><th>Lyrics</th><th>Translation</th></tr></thead><tbody>${Array.from(
        { length: rows },
      )
        .map(
          (_, i) =>
            `<tr><td class="num">${i + 1}</td><td>${nl2br(
              lyricsLines[i] ?? "",
            )}</td><td>${nl2br(translationLines[i] ?? "")}</td></tr>`,
        )
        .join("")}</tbody></table>`;
    }
  }

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>${escape(opts.title)} — Ojaswati</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; color: #111; max-width: 720px; margin: 0 auto; padding: 16px; line-height: 1.5; }
  h1 { font-size: 22pt; margin: 0 0 4px; font-family: Georgia, serif; }
  .subtitle { font-size: 10pt; color: #666; margin: 0 0 16px; }
  table.meta { border-collapse: collapse; margin: 0 0 24px; width: 100%; font-size: 10pt; }
  table.meta th { text-align: left; font-weight: 600; color: #444; padding: 4px 12px 4px 0; width: 90px; vertical-align: top; }
  table.meta td { padding: 4px 0; }
  .section { margin: 0 0 16px; padding: 10px 12px; border-left: 3px solid #8b5cf6; background: #faf7ff; border-radius: 0 4px 4px 0; page-break-inside: avoid; }
  .section h3 { font-size: 9pt; text-transform: uppercase; letter-spacing: 0.08em; color: #6d28d9; margin: 0 0 6px; }
  .sanskrit { font-family: Georgia, "Noto Serif Devanagari", "Tibetan Machine Uni", serif; font-size: 13pt; margin: 0 0 6px; white-space: pre-wrap; }
  .iast { font-style: italic; font-size: 10pt; color: #555; margin: 0 0 6px; white-space: pre-wrap; }
  .en { font-size: 10.5pt; color: #222; margin: 0; padding-left: 10px; border-left: 1px solid #ddd; white-space: pre-wrap; }
  table.verses { width: 100%; border-collapse: collapse; font-size: 10pt; }
  table.verses th, table.verses td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: top; text-align: left; }
  table.verses .num { color: #888; width: 24px; }
  .empty { color: #888; font-style: italic; }
  .footer { margin-top: 24px; padding-top: 8px; border-top: 1px solid #eee; font-size: 8pt; color: #888; }
  @media print {
    .print-controls { display: none; }
    body { padding: 0; }
  }
  .print-controls { position: fixed; top: 12px; right: 12px; }
  .print-controls button { padding: 8px 14px; font-size: 12pt; cursor: pointer; border: 1px solid #ccc; background: #fff; border-radius: 4px; }
</style>
</head>
<body>
  <div class="print-controls"><button onclick="window.print()">Print / Save as PDF</button></div>
  <h1>${escape(opts.title)}</h1>
  <p class="subtitle">Ojaswati Learning Library</p>
  <table class="meta"><tbody>${metaRows}</tbody></table>
  ${lyricsHtml}
  <p class="footer">Generated from ojaswati.com on ${escape(new Date().toLocaleString())}</p>
</body>
</html>`;
  return html;
}

/** Open the printable song page in a new tab and auto-trigger
 *  window.print() so the user can save as PDF via the browser dialog.
 *  Uses buildSongHtml() so the printed page is identical to the
 *  Drive-backed copy. */
function openPrintableSongPage(opts: PrintableSongOpts) {
  const baseHtml = buildSongHtml(opts);
  // Inject an auto-print script + a manual print button before </body>,
  // so the tab opens, fires print after a moment, and the user can
  // re-print from the visible button if the auto-print is dismissed.
  const html = baseHtml.replace(
    "</body>",
    `  <div class="print-controls" style="position:fixed;top:12px;right:12px;"><button onclick="window.print()" style="padding:8px 14px;font-size:12pt;cursor:pointer;border:1px solid #ccc;background:#fff;border-radius:4px;">Print / Save as PDF</button></div>
  <style>@media print { .print-controls { display: none; } }</style>
  <script>setTimeout(() => window.print(), 400);</script>
</body>`,
  );
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup blocked — fall back to download.
    const a = document.createElement("a");
    a.href = url;
    a.download = `${opts.title.replace(/[^\p{L}\p{N} _.-]+/gu, "_")}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* =====================================================================
   CanonicalLyricsView — clean canonical sections view (#263).

   Replaces the whisper-based 32-verse karaoke table for documented
   compositions. Shows Pallavi / Anupallavi / Charanam 1 / Charanam 2
   as labeled sections, each with Sanskrit + transliteration +
   translation. No timing — audio plays separately.

   #274 — Inline editing. Per user feedback ("even the refresh button
   has to be removed. We can allow editing of the lyrics and
   translation before saving"):
     • The Refresh-from-Gemini button is GONE. Canonical lyrics are
       owned by the user once cached — Gemini is just the starting
       point. If the user wants to re-fetch they can clear the cached
       canonical_lyrics manually.
     • Each verse's sanskrit / transliteration / translation is now a
       textarea — click and type. A "Save changes" button appears at
       the top of the card when there's anything unsaved; clicking it
       persists the edited array to the feedback sidecar (so the
       edits survive page reloads + show up in the PDF + Drive upload).
     • A "Reset" link reverts unsaved edits back to the last-saved
       version (the `sections` prop).
   ===================================================================== */
type CanonicalSection = {
  section: string;
  sanskrit: string;
  transliteration: string;
  translation: string;
};

function CanonicalLyricsView({
  sections,
  driveBadges,
  onSave,
}: {
  sections: CanonicalSection[];
  driveBadges?: { lyrics?: React.ReactNode; translation?: React.ReactNode };
  /** #274 — Persist user-edited canonical sections back to the feedback
   *  sidecar. Called only on explicit Save click — not on every keystroke
   *  (that'd hammer the PATCH endpoint). */
  onSave?: (edited: CanonicalSection[]) => void;
}) {
  // Local edit buffer. Initialised from `sections`; re-syncs whenever
  // the parent passes a fresh sections array (e.g. cache load completes,
  // or another tab edited the song). We compare by JSON because the
  // section objects are recreated on every render.
  const [edited, setEdited] = useState<CanonicalSection[]>(sections);
  const sectionsKey = useMemo(() => JSON.stringify(sections), [sections]);
  useEffect(() => {
    setEdited(sections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey]);

  const isDirty = useMemo(
    () => JSON.stringify(edited) !== sectionsKey,
    [edited, sectionsKey],
  );

  const updateField = (
    i: number,
    field: "sanskrit" | "transliteration" | "translation",
    value: string,
  ) => {
    setEdited((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-300" />
          Lyrics & Translation
          <span className="text-[10px] uppercase tracking-wider font-normal text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-500/20 rounded px-1.5 py-0.5">
            canonical
          </span>
        </h3>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {driveBadges?.lyrics}
          {driveBadges?.translation}
          {isDirty && (
            <>
              <button
                type="button"
                onClick={() => setEdited(sections)}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                title="Discard your unsaved edits"
              >
                Reset
              </button>
              <Button
                type="button"
                size="sm"
                onClick={() => onSave?.(edited)}
                disabled={!onSave}
                className="h-7 px-3 gap-1.5 text-[11px]"
                title="Persist your edits to the song's feedback sidecar"
              >
                <Save className="h-3 w-3" />
                Save changes
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Subtle hint about editability — only shown when nothing is
          dirty so it doesn't compete with the Save button. */}
      {!isDirty && (
        <p className="text-[11px] text-muted-foreground italic -mt-1">
          Click any verse text to edit. Changes are saved with the Save
          changes button once you've made one.
        </p>
      )}
      <div className="space-y-3">
        {edited.map((v, i) => (
          <div
            key={i}
            className="rounded border border-violet-200/40 dark:border-violet-500/20 bg-violet-50/30 dark:bg-violet-500/5 p-3 space-y-2"
          >
            <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">
              {v.section}
            </div>
            {/* Source-script lyric. Serif + whitespace-pre-wrap match the
                original read-only look so the visual layout doesn't shift
                when you switch modes. */}
            <CanonicalEditField
              value={v.sanskrit}
              onChange={(val) => updateField(i, "sanskrit", val)}
              placeholder="(lyrics)"
              className="text-sm leading-relaxed font-serif"
            />
            <CanonicalEditField
              value={v.transliteration}
              onChange={(val) => updateField(i, "transliteration", val)}
              placeholder="(transliteration — optional)"
              className="text-xs leading-relaxed text-muted-foreground italic"
            />
            <CanonicalEditField
              value={v.translation}
              onChange={(val) => updateField(i, "translation", val)}
              placeholder="(translation — optional)"
              className="text-xs leading-relaxed text-foreground/90 border-l-2 border-violet-300 dark:border-violet-500/40 pl-3"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* Auto-resizing textarea that mimics the read-only display when not
 * focused. Keeps the canonical card visually unchanged at rest. */
function CanonicalEditField({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // Auto-grow: re-measure on value change so the textarea always fits
  // its content (no inner scrollbar, no clipped lines).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full bg-transparent border-0 resize-none p-0 m-0",
        "focus:outline-none focus:ring-0",
        "whitespace-pre-wrap",
        // Subtle hover hint so the user discovers editability without
        // a visible border at rest.
        "hover:bg-violet-100/40 dark:hover:bg-violet-500/10 rounded transition-colors",
        className,
      )}
    />
  );
}

/* =====================================================================
   LyricsTranslationViewer — verse-aligned side-by-side reader.

   The shape of "lyrics" coming back from Fly (after #222) is plain
   source-language text with verses separated by blank lines — e.g.

       நாடியப்பொருள் கைக்கூடு

       நியானமும் புகழு முழ்டாம்

   The "translation" file is one verse per line in the target language.
   We zip them by index so verse-N lines up across columns. An optional
   transliteration column (IAST, Devanagari, etc.) can be toggled on
   when transliterations.json is available.

   The Print button opens a new window with a print-optimised HTML
   snapshot so the user can Cmd+P → Save as PDF — no backend PDF
   generation needed and the result looks identical to what they see
   on screen.
   ===================================================================== */
/* User-facing labels for the script picker. The Original entry maps to
 * `null` script — the viewer renders lyrics.txt directly (the actual
 * singer's script) instead of going through transliterations.json. */
const LYRICS_SCRIPT_OPTIONS: ReadonlyArray<{ value: string | null; label: string }> = [
  { value: null,          label: "Original (sung)" },
  { value: "iast",        label: "Roman / IAST" },
  { value: "tamil",       label: "Tamil" },
  { value: "devanagari",  label: "Sanskrit / Devanagari" },
];

// #266a — Only English translation is exposed in the picker for now.
// User: "If we are allowing only english translation, let us only allow
// that in the choice. let us not allow tamil and sanskrit." The backend
// translations.json still contains tamil/sanskrit if a job generated
// them, but we don't surface those as user-selectable options because
// translation quality on the non-English ones hasn't been validated.
// Add them back here when we're confident in the quality.
const TRANSLATION_LANG_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "english",   label: "English" },
];

/** #265 — Multi-select chip picker for translation languages.
 *
 *  Each option renders as a small toggle pill. Tap to add/remove that
 *  language from the selection. At least ONE language must stay
 *  selected at all times: clicking the only currently-selected chip
 *  is a no-op (otherwise the user could end up with an empty
 *  translation column).
 *
 *  Used for "Translation" only — lyrics stays single-select via the
 *  existing ColumnPicker dropdown.
 */
function TranslationLangChips({
  selected,
  onChange,
  options,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {options.map((opt) => {
        const isOn = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              if (isOn) {
                const next = selected.filter((l) => l !== opt.value);
                if (next.length === 0) return; // keep at least one
                onChange(next);
              } else {
                onChange([...selected, opt.value]);
              }
            }}
            className={cn(
              "px-2 py-0.5 text-[11px] rounded-md border transition-colors",
              isOn
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "border-border text-muted-foreground hover:bg-muted/40",
            )}
            aria-pressed={isOn}
            title={isOn ? `Hide ${opt.label} translation` : `Show ${opt.label} translation`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function LyricsTranslationViewer({
  lyrics, translation, transliterations, allTranslations,
  lyricsScript, setLyricsScript,
  translationLangs, setTranslationLangs,
  versesTimed, audioRef,
  songTitle, songMeta, driveBadges,
  feedback, onEditTranslation, onEditLyrics, onSetVerseFlag,
}: {
  lyrics: ArtifactState<string>;
  translation: ArtifactState<string>;
  transliterations: Record<string, string[]>;
  allTranslations: Record<string, string[]>;
  lyricsScript: string | null;
  setLyricsScript: (v: string | null) => void;
  /** #265 — Multi-select: zero or more translation languages shown
   *  stacked per verse. Enforced to be at least one language at all
   *  times (toggling the last selected language off is a no-op). */
  translationLangs: string[];
  setTranslationLangs: (v: string[]) => void;
  /** Per-verse timing data for karaoke. Empty array = no karaoke. */
  versesTimed?: { sequence: number; text: string; start: number; end: number; source_script: string }[];
  /** Ref to the page's audio element so we can subscribe to timeupdate
   *  and drive currentTime on click. */
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  songTitle?: string;
  songMeta?: SongMeta;
  driveBadges: { lyrics: React.ReactNode; translation: React.ReactNode };
  // #250 — user-feedback overlay
  feedback?: SongFeedback;
  /** Save a user-edited translation. Pass "" to clear an existing edit. */
  onEditTranslation?: (language: string, verseIdx: number, text: string) => void;
  /** Save a user-edited LYRICS line (source-script). Pass "" to clear. (#254) */
  onEditLyrics?: (script: string, verseIdx: number, text: string) => void;
  /** Set/clear a verse-quality flag. Pass null to clear. */
  onSetVerseFlag?: (language: string, verseIdx: number, flag: VerseFlag | null) => void;
}) {
  // #250/#254 — Which CELL is being inline-edited. Tagged tuple so the
  // translation editor and the new lyrics editor are mutually exclusive
  // (you can only edit one at a time). null when nothing's open.
  const [editing, setEditing] = useState<{ col: "lyrics" | "translation"; idx: number } | null>(null);
  const [editDraft, setEditDraft] = useState("");
  // Active verse for karaoke highlighting — index into lyricsVerses /
  // translationVerses. null = nothing playing in a known range.
  const [activeVerseIdx, setActiveVerseIdx] = useState<number | null>(null);
  const verseRowRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Subscribe to the audio element's timeupdate. We use the timing
  // data from verses.json (start/end per verse) to find which verse
  // matches the current playback position. Falls back to no karaoke
  // when verses.json wasn't generated (pre-#224) or has zero timings.
  useEffect(() => {
    const a = audioRef?.current;
    if (!a || !versesTimed || versesTimed.length === 0) return;
    const hasTimings = versesTimed.some((v) => v.end > v.start);
    if (!hasTimings) return;
    const onTime = () => {
      const t = a.currentTime;
      // Linear scan is fine — typical songs are 10–30 verses. Could
      // binary-search if we ever hit very long pieces.
      //
      // #252 — Two-pass match.
      //
      // Pass 1: exact containment — is `t` inside any verse's
      //   [start, end) range? This is the ideal case: highlight the
      //   verse currently being sung.
      //
      // Pass 2 (NEW): if no exact match, find the LAST verse whose
      //   start is at-or-before `t`. Whisper segments don't always
      //   butt up against each other — small gaps (1-3s) between
      //   adjacent verses are common, especially around stanza
      //   breaks. Without this fallback, the karaoke highlight goes
      //   blank during every gap and the auto-scroll stops too. User
      //   sees the lyrics frozen at verses 1-4 while the audio plays
      //   well past them ("at 2:17 the song and lyrics are different").
      //   Falling back to "the most recently started verse" gives
      //   continuous, intuitive highlighting through gaps.
      let found = -1;
      for (let i = 0; i < versesTimed.length; i++) {
        const v = versesTimed[i];
        if (t >= v.start && t < v.end) { found = i; break; }
      }
      if (found < 0) {
        for (let i = versesTimed.length - 1; i >= 0; i--) {
          if (versesTimed[i].start <= t) { found = i; break; }
        }
      }
      setActiveVerseIdx(found >= 0 ? found : null);
    };
    a.addEventListener("timeupdate", onTime);
    return () => a.removeEventListener("timeupdate", onTime);
  }, [audioRef, versesTimed]);

  // Smooth-scroll the active verse row into view as playback advances.
  // `block: "center"` keeps the active line in the middle of the
  // viewport so the user always sees one verse ahead and one behind.
  useEffect(() => {
    if (activeVerseIdx === null) return;
    const el = verseRowRefs.current[activeVerseIdx];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeVerseIdx]);

  const seekToVerse = (i: number) => {
    const v = versesTimed?.[i];
    const a = audioRef?.current;
    if (!a || !v || v.end <= v.start) return;
    a.currentTime = v.start;
    void a.play();
  };

  const canSeek = !!(versesTimed && versesTimed.some((v) => v.end > v.start));
  // Lyrics column verses — resolved from the selected script.
  //   null      → lyrics.txt (the source script; whatever the singer sings in)
  //   "iast"…   → transliterations.json[selected script]
  const lyricsVerses = useMemo(() => {
    if (lyricsScript && transliterations[lyricsScript]?.length) {
      return transliterations[lyricsScript];
    }
    return splitVerses(lyrics.status === "ready" ? lyrics.data ?? "" : "");
  }, [lyrics, transliterations, lyricsScript]);

  // #265 — Backward-compat shim. Most of the per-verse machinery
  // (edits, flags, file downloads) is keyed by a single language. We
  // derive a "primary" language = first selected, and keep that as
  // the source of truth for edits/flags. Stacked rendering uses the
  // FULL translationLangs array below for display.
  const translationLang = translationLangs[0] ?? "english";
  const setTranslationLang = (v: string) => {
    // Used by the legacy single-pick code paths (mobile picker drop
    // in particular). Replaces the entire selection with one language.
    setTranslationLangs([v]);
  };

  // Per-language verse arrays for stacked rendering. Keyed by lang.
  // Falls back to translation.txt for pre-#223 jobs that don't have
  // per-language JSON cached.
  const translationsByLang = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const lang of translationLangs) {
      if (allTranslations[lang]?.length) {
        out[lang] = allTranslations[lang];
      } else if (lang === "english") {
        // Legacy translation.txt is always English.
        out[lang] = splitTranslation(
          translation.status === "ready" ? translation.data ?? "" : "",
        );
      } else {
        out[lang] = [];
      }
    }
    return out;
  }, [translation, allTranslations, translationLangs]);

  // Primary language verses — for edit-cell / flag / single-language
  // legacy code paths.
  const translationVerses = useMemo(() => {
    return translationsByLang[translationLang] ?? [];
  }, [translationsByLang, translationLang]);

  // Hide script options that the backend didn't generate (e.g. a
  // pre-#223 job has no transliterations.json). "Original" stays no
  // matter what because it's served by lyrics.txt directly.
  const availableLyricsOptions = useMemo(
    () => LYRICS_SCRIPT_OPTIONS.filter(
      (o) => o.value === null || (transliterations[o.value]?.length ?? 0) > 0,
    ),
    [transliterations],
  );
  const availableTranslationOptions = useMemo(
    () => TRANSLATION_LANG_OPTIONS.filter(
      (o) => (allTranslations[o.value]?.length ?? 0) > 0,
    ),
    [allTranslations],
  );

  const isLoading = lyrics.status === "loading" || translation.status === "loading";
  const isReady = lyricsVerses.length > 0 || translationVerses.length > 0;
  const maxRows = Math.max(lyricsVerses.length, translationVerses.length);

  // #246 — Detect the PassthroughTranslator placeholder so we render a
  // friendly callout instead of showing "[english: translation engine
  // not configured] <verse>" 30 times in a row. The backend stamps
  // every verse with this prefix when the real CloudflareTranslator
  // isn't configured (CLOUDFLARE_ACCOUNT_ID / CLOUDFLARE_API_TOKEN
  // missing OR fly hasn't been redeployed with #225 yet). When true,
  // we hide the translation column entirely and surface a one-line
  // "translations pending" hint at the top of the table.
  const placeholderPattern = /^\[[a-z]+: translation engine not configured\]/i;
  const translationsAreStub =
    translationVerses.length > 0 &&
    translationVerses.every((v) => placeholderPattern.test(v.trim()));
  // Strip the prefix when only some verses are stubbed (mixed state
  // shouldn't happen but be defensive — render the actual text part).
  const cleanedTranslationVerses = useMemo(
    () =>
      translationVerses.map((v) =>
        v.replace(placeholderPattern, "").trim(),
      ),
    [translationVerses, placeholderPattern],
  );

  // Pick a CSS font stack that matches the lyrics column's current
  // selection (not just the song's source language) so the rendering
  // looks right even when the user is reading the song in Devanagari
  // even though the song was sung in Tamil.
  const lyricsFont = fontForScript(lyricsScript, songMeta);

  const handlePrint = () => {
    const html = buildPrintableHtml({
      title: songTitle || songMeta?.title || "Song",
      meta: songMeta,
      lyricsVerses,
      translationVerses,
      lyricsScript,
      translationLang,
      lyricsFontStack: cssFontStackForScript(lyricsScript, songMeta),
    });
    const w = window.open("", "_blank", "width=820,height=1000,noopener");
    if (!w) {
      toast.error("Pop-up blocked", {
        description: "Allow pop-ups on this site to open the printable view.",
      });
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  /* #246 — Redesigned as a practice document.
   *
   * The old card stuffed lyrics + translation into a 3-column grid
   * inside a tight border. Read like a CSV. User: "address lyrics
   * and translation which i am eagerly waiting. present in proper
   * format either as doc or something. dont have clumsy presentation".
   *
   * Now: a paper-feeling card with a real masthead (serif title,
   * composer/raga subtitle), two columns with proper typography +
   * generous line-height, alternating row tint for the eye, and an
   * explicit "translations pending" banner when the backend hasn't
   * been deployed with the real Cloudflare translator yet (instead
   * of showing the placeholder verbatim 30 times). */
  const documentSubtitle = [
    songMeta?.composer,
    songMeta?.raga,
    songMeta?.album,
    songMeta?.year,
    songMeta?.language,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div>
      {/* Toolbar — small + quiet so the practice document below is
          the visual hero. Save-as-PDF + Drive badges live here. */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
          <FileText className="h-3 w-3" /> Practice document
          {driveBadges.lyrics}
          {driveBadges.translation}
        </div>
        <button
          type="button"
          onClick={handlePrint}
          disabled={!isReady}
          className="text-[11px] inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 hover:bg-muted/40 disabled:opacity-50"
          title="Open a print-friendly view — Cmd+P → Save as PDF"
        >
          <FileText className="h-3 w-3" /> Save as PDF
        </button>
      </div>

      {/* Body */}
      {isLoading && (
        <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {!isLoading && !isReady && (
        <div className="text-xs text-muted-foreground/70 italic">— not generated —</div>
      )}
      {!isLoading && isReady && (
        <article
          className="rounded-xl border border-border bg-card shadow-sm overflow-hidden"
          style={{
            // Subtle warm tint so the doc reads as "paper", not "code".
            backgroundImage:
              "linear-gradient(180deg, rgba(255,247,233,0.6) 0%, rgba(255,253,247,0.3) 100%)",
          }}
        >
          {/* Masthead — song title + subtitle, serif for elegance. */}
          <header className="px-6 pt-6 pb-4 border-b border-border/60">
            <h2
              className="font-display text-2xl sm:text-3xl leading-tight tracking-tight text-foreground"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              {songTitle || songMeta?.title || "Song"}
            </h2>
            {documentSubtitle && (
              <p className="mt-1.5 text-sm text-muted-foreground">
                {documentSubtitle}
              </p>
            )}
          </header>

          {/* Translations-stub banner (#246) — only when the entire
              translation column is the PassthroughTranslator placeholder.
              Hides the column rather than showing the noisy debug text. */}
          {translationsAreStub && (
            <div className="mx-6 mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">
              <strong>Translations pending</strong> — the lyrics below are
              ready, but the translation engine isn't activated yet on the
              backend. Once <code>fly deploy</code> runs with the Cloudflare
              secrets set, re-process this song to fill in the right column.
            </div>
          )}

          {/* Column headers + per-column dropdown pickers. Sit on a
              quiet surface so the masthead remains the visual anchor.
              #250 — Responsive: 2-col on mobile (#, Lyrics), 3-col on
              sm+ (#, Lyrics, Translation). On mobile the translation
              header is hidden because each verse's translation will
              stack below its lyrics row. */}
          <div className="grid grid-cols-[2rem_1fr] sm:grid-cols-[2rem_1fr_1fr] gap-x-6 px-4 sm:px-6 mt-4 pb-2 border-b border-border/50">
            <span className="text-right text-[10px] uppercase tracking-wider text-muted-foreground/70">
              #
            </span>
            <ColumnPicker
              labelPrefix="Lyrics"
              value={lyricsScript ?? "__source__"}
              onChange={(v) => setLyricsScript(v === "__source__" ? null : v)}
              options={availableLyricsOptions.map((o) => ({
                value: o.value ?? "__source__",
                label: o.value === null
                  ? `${o.label}${songMeta?.language && songMeta.language !== "Auto-detect" ? ` — ${songMeta.language}` : ""}`
                  : o.label,
              }))}
            />
            {translationsAreStub ? (
              <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-muted-foreground/70 italic">
                Translation (pending)
              </span>
            ) : (
              <div className="hidden sm:block">
                {/* #265 — Multi-select chip picker for translation
                    languages. Tap to toggle each language on/off; at
                    least one must stay selected (clicking the last
                    selected chip is a no-op). All selected languages
                    are stacked under each verse below. */}
                <TranslationLangChips
                  selected={translationLangs}
                  onChange={setTranslationLangs}
                  options={availableTranslationOptions}
                />
              </div>
            )}
          </div>

          {/* #250 — On mobile only, a second header row for the
              translation column picker. Sits above the verse list so
              the user can switch language without scrolling. */}
          {!translationsAreStub && (
            <div className="sm:hidden px-4 pt-2 pb-2 border-b border-border/50 flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 shrink-0">
                Translation
              </span>
              {/* #265 — Same multi-select chip picker as desktop,
                  works on mobile (tap-friendly). */}
              <TranslationLangChips
                selected={translationLangs}
                onChange={setTranslationLangs}
                options={availableTranslationOptions}
              />
            </div>
          )}
          {/* Hide the legacy single-pick mobile picker now that chips
              replace it: */}
          {false && !translationsAreStub && (
            <div className="sm:hidden px-4 pt-2 pb-2 border-b border-border/50 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                Translation
              </span>
              <ColumnPicker
                labelPrefix=""
                value={translationLang}
                onChange={setTranslationLang}
                options={availableTranslationOptions.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </div>
          )}

          {/* Verse rows — generous padding, alternating tint, ample
              line-height so the document reads like a practice sheet
              instead of a spreadsheet.

              #250 — Three layered enhancements:
                a) Responsive layout: on mobile the translation stacks
                   below the lyrics (col-span-2 with indent); on sm+
                   they sit side-by-side.
                b) Editable translation: click the translation cell to
                   open an inline textarea. Save on blur or Enter.
                c) Per-verse flags: 👍 / 👎 buttons next to the verse
                   number for "AI got this right/wrong" triage.

              Seek-to-verse stays as the LYRICS row's click target so
              it doesn't conflict with the translation edit gesture. */}
          <div className="max-h-[28rem] overflow-y-auto">
            {Array.from({ length: maxRows }).map((_, i) => {
              const leftModel = lyricsVerses[i] || "";
              const rightModel = translationsAreStub
                ? ""
                : (cleanedTranslationVerses[i] || translationVerses[i] || "");
              // #254 — Lyrics edit overlay. Key by the EFFECTIVE script:
              // when the user has "Original (sung)" selected (null), the
              // edit lives under the song's source_script so it shows up
              // whether you view via Original or via that script's
              // explicit name in the dropdown.
              const effectiveScript =
                lyricsScript ?? versesTimed?.[0]?.source_script ?? "source";
              const userLyricsEdit = getEditedLyrics(feedback, effectiveScript, i);
              const leftDisplay = userLyricsEdit ?? leftModel;
              const isLyricsEdited = userLyricsEdit !== null;
              // #250 — Translation edit overlay
              const userEdit = getEditedTranslation(feedback, translationLang, i);
              const rightDisplay = userEdit ?? rightModel;
              const isEdited = userEdit !== null;
              const flag = getVerseFlag(feedback, translationLang, i);
              const isActive = activeVerseIdx === i;
              const isEditingTranslation = editing?.col === "translation" && editing.idx === i;
              const isEditingLyrics = editing?.col === "lyrics" && editing.idx === i;
              const stripe = i % 2 === 1;
              return (
                <div
                  key={i}
                  ref={(el) => { verseRowRefs.current[i] = el; }}
                  className={cn(
                    "grid grid-cols-[2rem_1fr] sm:grid-cols-[2rem_1fr_1fr] gap-x-4 sm:gap-x-6 px-4 sm:px-6 py-3 sm:py-4 transition-colors",
                    stripe && "bg-foreground/[0.015] dark:bg-foreground/[0.03]",
                    // Karaoke highlight overrides stripe.
                    isActive && "bg-amber-500/15 ring-1 ring-amber-500/40 ring-inset",
                  )}
                >
                  {/* Verse # + flag controls. The number doubles as
                      the seek target when timings are available. */}
                  <div className="flex flex-col items-end gap-1.5 pt-1">
                    <button
                      type="button"
                      onClick={canSeek ? () => seekToVerse(i) : undefined}
                      disabled={!canSeek}
                      className={cn(
                        "text-right text-[11px] tabular-nums leading-none",
                        canSeek && "cursor-pointer hover:text-amber-600",
                        isActive
                          ? "text-amber-700 dark:text-amber-400 font-semibold"
                          : "text-muted-foreground/60",
                      )}
                      title={canSeek ? `Play from ${formatSeconds(versesTimed![i]?.start ?? 0)}` : undefined}
                      aria-label={canSeek ? `Play verse ${i + 1}` : `Verse ${i + 1}`}
                    >
                      {i + 1}
                    </button>
                    {!translationsAreStub && onSetVerseFlag && (
                      <VerseFlagButtons
                        flag={flag}
                        onSet={(next) => onSetVerseFlag(translationLang, i, next)}
                      />
                    )}
                  </div>

                  {/* Lyrics cell — clickable to seek; pencil-on-hover
                      to fix whisper transcription mistakes (#254). The
                      seek click stays on the cell BACKGROUND so the
                      gesture stays natural; the pencil sits absolutely
                      positioned so it doesn't fight the seek click. */}
                  <div
                    className={cn(
                      "whitespace-pre-wrap break-words leading-[1.7] text-[15px]",
                      isActive ? "font-semibold text-base" : "font-normal text-foreground",
                    )}
                    style={{ fontFamily: lyricsFont }}
                  >
                    {isEditingLyrics ? (
                      <EditableTranslationCell
                        initial={editDraft}
                        onCancel={() => setEditing(null)}
                        onSave={(text) => {
                          if (onEditLyrics) {
                            // Same "edit equals model → clear" rule as
                            // the translation editor — so user can't
                            // accidentally pin in the whisper output
                            // and miss future re-process improvements.
                            const normalized = text.trim() === leftModel.trim() ? "" : text;
                            onEditLyrics(effectiveScript, i, normalized);
                          }
                          setEditing(null);
                        }}
                      />
                    ) : (
                      <div className="group relative">
                        <div
                          onClick={canSeek ? () => seekToVerse(i) : undefined}
                          className={canSeek ? "cursor-pointer" : undefined}
                          title={canSeek ? "Tap to play from this verse" : undefined}
                        >
                          {leftDisplay || <span className="text-muted-foreground/40">—</span>}
                          {isLyricsEdited && (
                            <span
                              className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 align-middle"
                              title="You edited the lyrics for this verse. Re-processing won't overwrite your edit."
                            >
                              <Pencil className="h-2.5 w-2.5" /> edited
                            </span>
                          )}
                        </div>
                        {onEditLyrics && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditDraft(leftDisplay);
                              setEditing({ col: "lyrics", idx: i });
                            }}
                            className="absolute -top-1 right-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground/60 hover:text-foreground p-1"
                            title="Edit the lyrics — fix whisper transcription mistakes"
                            aria-label={`Edit lyrics for verse ${i + 1}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Translation cell — editable on click. On mobile,
                      stacks below the lyrics (col-span-2 + indent). */}
                  <div
                    className={cn(
                      "col-span-2 sm:col-span-1 pl-9 sm:pl-0 mt-1 sm:mt-0",
                      "whitespace-pre-wrap break-words leading-[1.7] text-[14px]",
                      isActive ? "font-semibold text-[15px] text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {translationsAreStub ? (
                      <span className="text-muted-foreground/30 italic text-xs">
                        — pending —
                      </span>
                    ) : isEditingTranslation ? (
                      <EditableTranslationCell
                        initial={editDraft}
                        onCancel={() => setEditing(null)}
                        onSave={(text) => {
                          // Empty string clears the edit; non-empty
                          // saves it. Either way, close the editor.
                          if (onEditTranslation) {
                            // Treat "same as model output" as clearing,
                            // so the user doesn't accidentally lock in
                            // a duplicate that prevents future model
                            // updates from showing.
                            const normalized = text.trim() === rightModel.trim() ? "" : text;
                            onEditTranslation(translationLang, i, normalized);
                          }
                          setEditing(null);
                        }}
                      />
                    ) : (
                      <div className="group relative">
                        <div>
                          {rightDisplay || <span className="text-muted-foreground/40">—</span>}
                          {isEdited && (
                            <span
                              className="ml-2 inline-flex items-center gap-0.5 text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 align-middle"
                              title="You edited this verse. Re-processing the song won't overwrite your edit."
                            >
                              <Pencil className="h-2.5 w-2.5" /> edited
                            </span>
                          )}
                        </div>
                        {onEditTranslation && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditDraft(rightDisplay);
                              setEditing({ col: "translation", idx: i });
                            }}
                            className="absolute -top-1 right-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-muted-foreground/60 hover:text-foreground p-1"
                            title="Edit this translation"
                            aria-label={`Edit translation for verse ${i + 1}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                    {/* #265 — Additional translations stacked below the
                        editable primary cell. Read-only here (edits flow
                        through the primary language only — switch the
                        chip selection to make a different language
                        editable). Each gets a small uppercase label so
                        the user can tell them apart. */}
                    {translationLangs.length > 1 && (
                      <div className="space-y-2 mt-3 pt-2 border-t border-border/40">
                        {translationLangs
                          .filter((l) => l !== translationLang)
                          .map((lang) => {
                            const langEdit = getEditedTranslation(feedback, lang, i);
                            const display =
                              langEdit ?? (translationsByLang[lang]?.[i] ?? "");
                            if (!display) return null;
                            const label =
                              TRANSLATION_LANG_OPTIONS.find((o) => o.value === lang)
                                ?.label ?? lang;
                            return (
                              <div
                                key={lang}
                                className="text-[13px] leading-relaxed"
                              >
                                <span className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/60 mr-2 align-top">
                                  {label}
                                </span>
                                <span className="text-muted-foreground/90">
                                  {display}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer note — credits the engine + counts so the user
              feels they're holding a real artifact, not raw output. */}
          <footer className="px-6 py-3 border-t border-border/50 text-[11px] text-muted-foreground/70 flex items-center justify-between flex-wrap gap-2">
            <span>{maxRows} verse{maxRows === 1 ? "" : "s"}</span>
            <span className="italic">Ojaswati Learning Library</span>
          </footer>
        </article>
      )}
    </div>
  );
}

/* ---------- #250 — Edit + flag affordances for translation cells ---------- */

/**
 * Inline editor for a single translation verse. Auto-focuses, saves
 * on blur or Cmd/Ctrl+Enter, cancels on Esc. The save handler decides
 * whether the text counts as an edit or a clear (empty string = clear
 * the user override).
 */
function EditableTranslationCell({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState(initial);
  // Autofocus + place cursor at end on mount.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  return (
    <div className="space-y-1.5">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => onSave(value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
          // Cmd+Enter (Mac) or Ctrl+Enter — save without leaving the
          // cell. Just Enter inserts a newline because translations
          // can be multi-line for long verses.
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSave(value);
          }
        }}
        rows={Math.max(2, value.split("\n").length + 1)}
        className="w-full resize-none rounded-md border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[14px] leading-[1.7] text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/40"
        placeholder="Translation — leave blank to clear your edit"
      />
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span>
          <kbd className="px-1 py-0.5 border border-border rounded text-[9px]">⌘↩</kbd> save
          {" · "}
          <kbd className="px-1 py-0.5 border border-border rounded text-[9px]">Esc</kbd> cancel
          {" · "}
          blur saves
        </span>
        <button
          type="button"
          onMouseDown={(e) => {
            // mousedown so it fires BEFORE blur (which would trigger
            // save with the current draft).
            e.preventDefault();
            onSave("");
          }}
          className="ml-auto text-muted-foreground hover:text-destructive underline"
          title="Remove your edit and revert to the AI's translation"
        >
          clear my edit
        </button>
      </div>
    </div>
  );
}

/**
 * 👍 / 👎 cluster for per-verse quality flagging. Three states:
 *   - flag = null  → both buttons quiet; click sets up/down
 *   - flag = "up"  → 👍 lit; click again clears
 *   - flag = "down" → 👎 lit; click again clears
 * Tiny on purpose — these sit under the verse number so they don't
 * fight the lyrics for attention.
 */
function VerseFlagButtons({
  flag,
  onSet,
}: {
  flag: VerseFlag | null;
  onSet: (next: VerseFlag | null) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSet(flag === "up" ? null : "up");
        }}
        className={cn(
          "p-0.5 rounded transition-colors",
          flag === "up"
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-muted-foreground/30 hover:text-emerald-600/70",
        )}
        title={flag === "up" ? "You marked this verse correct — click to clear" : "Mark this translation correct"}
        aria-label="Mark verse translation correct"
      >
        <ThumbsUp className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSet(flag === "down" ? null : "down");
        }}
        className={cn(
          "p-0.5 rounded transition-colors",
          flag === "down"
            ? "text-destructive"
            : "text-muted-foreground/30 hover:text-destructive/70",
        )}
        title={flag === "down" ? "You marked this verse wrong — click to clear" : "Mark this translation wrong"}
        aria-label="Mark verse translation wrong"
      >
        <ThumbsDown className="h-3 w-3" />
      </button>
    </div>
  );
}

/** Per-column dropdown header — used twice, once per column. Keeps the
 *  picker visually quiet (looks like a tiny label, expands to native
 *  <select> on click) so the header isn't dominated by big controls. */
function ColumnPicker({
  labelPrefix, value, onChange, options,
}: {
  labelPrefix: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  // Single-option degenerate case: show the label as a static chip
  // (no dropdown affordance) — matches the rest of the header's typography.
  if (options.length <= 1) {
    const only = options[0];
    return (
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {labelPrefix}{only ? ` — ${only.label}` : ""}
      </span>
    );
  }
  return (
    <label className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
      {labelPrefix}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[11px] normal-case bg-background/60 border border-border rounded px-1.5 py-0.5"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Inline style font for the lyrics column. Differs from indicFontFor
 *  in that the script is the USER'S currently-selected view (e.g. the
 *  user might be reading a Tamil song in Devanagari right now). */
function fontForScript(script: string | null, songMeta: SongMeta | undefined): string {
  // If user picked Original, use the source language's font.
  if (script === null) return indicFontFor(songMeta);
  if (script === "tamil")       return "'Noto Sans Tamil', 'Tamil MN', system-ui, sans-serif";
  if (script === "devanagari")  return "'Noto Sans Devanagari', 'Devanagari MT', system-ui, sans-serif";
  if (script === "iast")        return "system-ui, -apple-system, sans-serif";
  return "system-ui, sans-serif";
}

function cssFontStackForScript(script: string | null, songMeta: SongMeta | undefined): string {
  return fontForScript(script, songMeta);
}

/** Split lyrics.txt into verses on blank lines. Robust to trailing
 *  whitespace and CRLF line endings. */
function splitVerses(raw: string): string[] {
  return raw
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Split translation.txt: one verse per line. Strip the old "N. "
 *  numbering prefix in case we're looking at a pre-#222 artifact. */
function splitTranslation(raw: string): string[] {
  return raw
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((s) => s.replace(/^\s*\d+\.\s*/, "").trim())
    .filter(Boolean);
}

/** Pick a font stack that renders the song's script properly. The system
 *  font usually does for SOMETHING, but Devanagari / Tamil look much
 *  better with their respective web-safe stacks. */
function indicFontFor(meta: SongMeta | undefined): string {
  const lang = meta?.language ?? "";
  if (/tamil/i.test(lang))  return "'Noto Sans Tamil', 'Tamil MN', system-ui, sans-serif";
  if (/sanskrit|hindi|marathi/i.test(lang)) return "'Noto Sans Devanagari', 'Devanagari MT', system-ui, sans-serif";
  if (/telugu/i.test(lang))    return "'Noto Sans Telugu', 'Telugu MN', system-ui, sans-serif";
  if (/kannada/i.test(lang))   return "'Noto Sans Kannada', 'Kannada MN', system-ui, sans-serif";
  if (/malayalam/i.test(lang)) return "'Noto Sans Malayalam', 'Malayalam MN', system-ui, sans-serif";
  if (/bengali/i.test(lang))   return "'Noto Sans Bengali', 'Bangla MN', system-ui, sans-serif";
  if (/gujarati/i.test(lang))  return "'Noto Sans Gujarati', 'Gujarati MT', system-ui, sans-serif";
  return "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
}

/** Title-cased label for the lyrics-column script header in both the
 *  in-app viewer and the printable export. Falls back to the raw tag if
 *  the user customised the backend to emit an unknown one. */
function prettyScriptLabel(tag: string): string {
  switch (tag) {
    case "iast":       return "Roman / IAST";
    case "tamil":      return "Tamil";
    case "devanagari": return "Devanagari";
    case "telugu":     return "Telugu";
    case "kannada":    return "Kannada";
    case "malayalam":  return "Malayalam";
    case "bengali":    return "Bengali";
    case "gujarati":   return "Gujarati";
    default:           return tag.charAt(0).toUpperCase() + tag.slice(1);
  }
}

function prettyLangLabel(tag: string): string {
  switch (tag) {
    case "english":   return "English";
    case "tamil":     return "Tamil";
    case "sanskrit":  return "Sanskrit";
    default:          return tag.charAt(0).toUpperCase() + tag.slice(1);
  }
}

/** Format a number of seconds as M:SS for the karaoke seek tooltip. */
function formatSeconds(s: number): string {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

/** Build a print-optimised standalone HTML document for the popup. Uses
 *  Noto Sans web fonts (Google Fonts CDN) for proper Devanagari/Tamil
 *  rendering, and a clean two-column table layout. The window's print
 *  dialog opens automatically when the page loads.
 *
 *  Receives the user's CURRENT viewer selections (#223) so the printed
 *  output matches what they see on screen — lyrics in their chosen
 *  script, translation in their chosen language. */
function buildPrintableHtml(args: {
  title: string;
  meta?: SongMeta;
  lyricsVerses: string[];
  translationVerses: string[];
  lyricsScript: string | null;
  translationLang: string;
  lyricsFontStack: string;
}): string {
  const safe = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const rows = Math.max(args.lyricsVerses.length, args.translationVerses.length);

  const lyricsHeader =
    args.lyricsScript === null
      ? `Lyrics${args.meta?.language && args.meta.language !== "Auto-detect" ? ` — ${args.meta.language}` : ""}`
      : `Lyrics — ${prettyScriptLabel(args.lyricsScript)}`;
  const translationHeader = `Translation — ${prettyLangLabel(args.translationLang)}`;

  const metaLine = [
    args.meta?.composer && `Composer: ${args.meta.composer}`,
    args.meta?.raga && `Raga: ${args.meta.raga}`,
    args.meta?.album && `Album: ${args.meta.album}`,
    args.meta?.year && `Year: ${args.meta.year}`,
    args.meta?.language && args.meta.language !== "Auto-detect" && `Language: ${args.meta.language}`,
  ].filter(Boolean).join(" · ");

  const verseRows = Array.from({ length: rows }).map((_, i) => {
    const l = safe(args.lyricsVerses[i] || "");
    const t = safe(args.translationVerses[i] || "");
    return `<tr><td class="num">${i + 1}</td><td class="src">${l.replace(/\n/g, "<br/>")}</td><td class="tgt">${t.replace(/\n/g, "<br/>")}</td></tr>`;
  }).join("\n");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${safe(args.title)} — lyrics</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600&family=Noto+Sans+Tamil:wght@400;600&family=Noto+Sans+Telugu&family=Noto+Sans+Kannada&family=Noto+Sans+Malayalam&family=Noto+Sans+Bengali&family=Noto+Sans+Gujarati&display=swap" />
<style>
  :root {
    --ink: #1c1917;
    --soft: #57534e;
    --accent: #b45309;
    --rule: #e7e5e4;
    --bg: #ffffff;
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: var(--bg); color: var(--ink); font: 11pt/1.55 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  body { padding: 16mm; max-width: 210mm; margin: 0 auto; }
  h1 { font-size: 20pt; margin: 0 0 4pt; letter-spacing: -0.01em; }
  .meta { color: var(--soft); font-size: 10pt; margin-bottom: 14pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { vertical-align: top; padding: 6pt 8pt; border-bottom: 1px solid var(--rule); font-size: 10.5pt; }
  th { text-align: left; font-size: 9pt; text-transform: uppercase; letter-spacing: 0.04em; color: var(--accent); border-bottom: 2px solid var(--accent); }
  td.num { color: var(--soft); font-size: 8.5pt; text-align: right; width: 18pt; padding-top: 9pt; }
  td.src { font-family: ${args.lyricsFontStack}; font-weight: 450; }
  td.tgt { color: var(--ink); }
  footer { margin-top: 14pt; color: var(--soft); font-size: 9pt; border-top: 1px solid var(--rule); padding-top: 6pt; }
  .actions { position: fixed; top: 8mm; right: 8mm; font-size: 10pt; }
  .actions button { padding: 4pt 10pt; border: 1px solid var(--rule); background: white; cursor: pointer; border-radius: 4pt; font: inherit; color: var(--ink); }
  .actions button + button { margin-left: 4pt; }
  @page { size: A4; margin: 0; }
  @media print {
    body { padding: 14mm 14mm; }
    .actions { display: none; }
  }
</style>
</head>
<body>
  <div class="actions">
    <button onclick="window.print()">Save as PDF / Print</button>
    <button onclick="window.close()">Close</button>
  </div>

  <h1>${safe(args.title)}</h1>
  ${metaLine ? `<div class="meta">${safe(metaLine)}</div>` : ""}

  <table>
    <thead>
      <tr>
        <th></th>
        <th>${safe(lyricsHeader)}</th>
        <th>${safe(translationHeader)}</th>
      </tr>
    </thead>
    <tbody>
      ${verseRows}
    </tbody>
  </table>

  <footer>Generated by ojaswati.com — ${rows} verse${rows === 1 ? "" : "s"}</footer>

  <script>
    // Auto-open the print dialog ~250ms after fonts settle. The user can
    // cancel and just read, or hit "Save as PDF" in the dialog.
    window.addEventListener("load", function() {
      setTimeout(function() { window.print(); }, 300);
    });
  </script>
</body>
</html>`;
}

/* ---------- Tiny helpers ---------- */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center text-xs text-muted-foreground py-8 px-4">
      {children}
    </div>
  );
}

/* =====================================================================
   WhatsAppInbox — pending voice notes from the Meta Cloud API webhook
   (#275). iPhone-friendly Voice Drop ingest path.

   Polls /api/voice-drop/inbox every 5 seconds and shows pending items.
   Tap a row → fetch the bytes from /api/voice-drop/file/:id → call
   onStage(file) which acceptFile()s it into the Voice Drop dropzone.
   Dismiss button POSTs to /api/voice-drop/dismiss/:id.

   Hidden when the inbox is empty AND there's no setup error — keeps
   the tab clean for users who haven't wired up WhatsApp yet. A small
   "Setup" link drops them into docs/WHATSAPP_SETUP.md so the path is
   discoverable.
   ===================================================================== */
type WhatsAppInboxItem = {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  duration_seconds: number | null;
  received_at: number;
  wa_id: string;
};

function WhatsAppInbox({ onStage }: { onStage: (f: File) => void }) {
  const [items, setItems] = useState<WhatsAppInboxItem[]>([]);
  const [stagingId, setStagingId] = useState<string | null>(null);
  // Show the panel after the first successful fetch, even if empty,
  // so the user sees the "WhatsApp Inbox" affordance and the "Setup"
  // link. Stays hidden if fetch keeps failing (likely 401 because
  // they aren't signed in or the endpoint isn't deployed yet).
  const [reachable, setReachable] = useState(false);
  const lastFetchAt = useRef(0);

  // Polling. 5-second cadence is a reasonable balance: voice notes
  // are infrequent + the user will glance at the panel once they
  // send one, so faster polling wouldn't add real value.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await fetch("/api/voice-drop/inbox", {
          credentials: "include",
        });
        if (cancelled) return;
        if (res.ok) {
          const j = (await res.json()) as { items?: WhatsAppInboxItem[] };
          setItems(j.items ?? []);
          setReachable(true);
          lastFetchAt.current = Date.now();
        } else if (res.status !== 401) {
          // 401 is expected before sign-in; quietly stay hidden.
          setReachable(true);
        }
      } catch {
        /* network blip — try again next tick */
      }
    };
    void tick();
    const handle = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  const stage = async (item: WhatsAppInboxItem) => {
    if (stagingId) return;
    setStagingId(item.id);
    try {
      const res = await fetch(`/api/voice-drop/file/${encodeURIComponent(item.id)}`, {
        credentials: "include",
      });
      if (!res.ok) {
        toast.error("Couldn't load voice note", {
          description: `HTTP ${res.status}`,
        });
        return;
      }
      const blob = await res.blob();
      const filename =
        decodeURIComponent(res.headers.get("x-filename") || item.filename || "whatsapp.ogg");
      onStage(new File([blob], filename, { type: blob.type || item.mime_type }));
      // Fire-and-forget dismiss so the row leaves the list. We don't
      // wait for it before optimistically removing.
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      void fetch(`/api/voice-drop/dismiss/${encodeURIComponent(item.id)}`, {
        method: "POST",
        credentials: "include",
      }).catch(() => undefined);
    } finally {
      setStagingId(null);
    }
  };

  const dismiss = async (id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    await fetch(`/api/voice-drop/dismiss/${encodeURIComponent(id)}`, {
      method: "POST",
      credentials: "include",
    }).catch(() => undefined);
  };

  // Hide the whole panel until we've reached the endpoint at least once.
  if (!reachable) return null;

  return (
    <SectionCard className="border-emerald-300/40 bg-emerald-50/40 dark:bg-emerald-500/5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Smartphone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          WhatsApp Inbox
          {items.length > 0 && (
            <span className="text-[10px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-500/20 rounded px-1.5 py-0.5">
              {items.length} new
            </span>
          )}
        </h3>
        <a
          href="https://github.com/s0123uma/ojaswati/blob/main/docs/WHATSAPP_SETUP.md"
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          title="One-time setup walkthrough"
        >
          Setup
        </a>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground mt-2">
          No new voice notes. Send one to your WhatsApp Business number from
          your iPhone — it'll appear here within ~5 seconds.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-center gap-3 rounded-lg border border-emerald-200/50 dark:border-emerald-500/20 bg-card/60 p-2.5"
            >
              <div className="h-9 w-9 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center shrink-0">
                <FileAudio className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">
                  Voice note from +{it.wa_id}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {it.duration_seconds ? `${it.duration_seconds}s · ` : ""}
                  {(it.size_bytes / 1024).toFixed(0)} KB ·{" "}
                  {new Date(it.received_at * 1000).toLocaleString()}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => void stage(it)}
                disabled={stagingId === it.id}
                className="h-8 px-3 text-xs"
                title="Stage this voice note in the dropzone"
              >
                {stagingId === it.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  "Use"
                )}
              </Button>
              <button
                type="button"
                onClick={() => void dismiss(it.id)}
                className="text-muted-foreground hover:text-foreground p-1"
                title="Dismiss without staging"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function StepOverlay({
  title, apiUrl, labels, statuses, onCancel, children,
}: { title: string; apiUrl: string; labels: string[]; statuses: StepStatus[]; onCancel: () => void; children?: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-background/85 backdrop-blur-md flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg">{title}</div>
            <div className="text-xs text-muted-foreground">
              Live from <span className="font-mono text-primary">{apiUrl}</span>
            </div>
          </div>
          <button onClick={onCancel} className="text-[11px] text-muted-foreground hover:text-destructive border border-border rounded-md px-2 py-1">
            Cancel
          </button>
        </div>
        <ol className="space-y-2.5">
          {labels.map((s, i) => {
            const st = statuses[i];
            return (
              <li key={i} className="flex items-center gap-3 text-sm">
                <span className={cn(
                  "h-5 w-5 rounded-full flex items-center justify-center text-[10px] shrink-0 border",
                  st === "done" && "bg-primary/20 border-primary/40 text-primary",
                  st === "running" && "border-primary text-primary",
                  st === "error" && "bg-destructive/20 border-destructive/50 text-destructive",
                  st === "pending" && "border-border text-muted-foreground"
                )}>
                  {st === "done" ? <Check className="h-3 w-3" /> :
                   st === "running" ? <Loader2 className="h-3 w-3 animate-spin" /> :
                   st === "error" ? <AlertCircle className="h-3 w-3" /> : i + 1}
                </span>
                <span className={cn(
                  st === "running" && "text-foreground",
                  st === "done" && "text-muted-foreground line-through",
                  st === "error" && "text-destructive",
                  st === "pending" && "text-muted-foreground/70"
                )}>{s}</span>
              </li>
            );
          })}
        </ol>
        {children && <div className="mt-5">{children}</div>}
      </div>
    </div>
  );
}
